import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef, useCallback, useMemo } from "react";
// @ts-ignore
import VisGraph from "react-vis-graph-wrapper";
import { DataSet, Network } from "vis-network/standalone";
import {
  getGraphVizBehaviourOptions,
} from "./GraphVizConstants";
import "./OrgGraphVis.css";
import "./SearchHighlight.css";
import "./HoverDataUtils.css";
import OrgGraphTopPanel from "./OrgGraphTopPanel";
import { Box, useTheme, Paper, Typography, IconButton, Snackbar, Button } from "@mui/material";
import { ConnectionPath, findConnectionPath, getBranchToHide } from './connectionPath';
import ConnectionPathDetails from './ConnectionPathDetails';
import CloseIcon from '@mui/icons-material/Close';
import OrgGraphContextMenu, { OrgGraphContextMenuProps } from "./OrgGraphContextMenu";
import StreetViewPopup from "./StreetViewPopup";
import { Edge, Node, OrgGraphState, NodeLabelSettings, EdgeLabelSettings } from "./types";
import SearchHighlightPanel, { SearchTerm } from './SearchHighlightPanel';
import { filterRelationshipsByDate, getEdgesForGraphType } from "./graphFilter";
import { HiddenStateStack } from "./hiddenStateStack";
import { viewportStateManager } from './viewportStateManager';
import { getContextId } from "./sessionStorage";
import { loadNodeLabelSettings, saveNodeLabelSettings, loadEdgeLabelSettings, saveEdgeLabelSettings } from './userSettings';
import EntityListPanel, { EntityListEntry } from './EntityListPanel';
import { getGeographicNodeIds } from './geographicSelection';
import { processGraphEntities } from './entityProcessor';
import {
  getAllNodesWithLabels,
  getAllEdgesWithLabels,
  extractUniqueGraphTypes,
  getGraphTypesCompositeKey,
  getHighlightedNodes,
  ApplyNodeStyling
} from './lib/styleProcessor';
import { mergeGraphNodeProperties } from './lib/nodePropertyUtils';
import { drawOverlayIcons } from './lib/canvasUtils';
import {
  getBadgeColor,
  getCarBadgeLabel,
  getNodeBadges,
  countOrganizationsWithTenantCases,
  layoutBadges,
  LEASED_CARS_ICON,
  TENANT_CASE_BADGE_COLOR,
} from './lib/badgeUtils';
import { useGraphEvents } from './hooks/useGraphEvents';
import { useGraphExpansion } from './hooks/useGraphExpansion';
import { useExpansionSlideAnimation } from './hooks/useExpansionSlideAnimation';
import GraphLoadingOverlay from './GraphLoadingOverlay';
import GraphStatusPill from './GraphStatusPill';
import { NodeCommentTarget, getNodeCommentTarget } from "./nodeComments";
import './GraphAnimations.css';
import { useTranslation } from 'react-i18next';

// Add interface for the exposed methods
export interface OrgGraphVisRef {
  getGraphState: () => OrgGraphState | null;
  exportGraphAsPNG: (fileName?: string, scale?: number) => Promise<void>;
  exportGraphAsSVG: (fileName?: string) => Promise<void>;
  getMaxScale: () => number;
  getNodeLabelSettings: () => NodeLabelSettings;
  setNodeLabelSettings: (settings: NodeLabelSettings) => void;
  getEdgeLabelSettings: () => EdgeLabelSettings;
  setEdgeLabelSettings: (settings: EdgeLabelSettings) => void;
}

interface OrgGraphVisProps {
  nodes: Node[];
  relationships: Edge[];
  orgNumber: string;
  graphMetaData: any;
  showFilterButtons: boolean;
  getAccessToken: () => string | undefined;
  hiddenStateStack?: HiddenStateStack;
  selectedGraphTypes?: string[];
  showBadPathsOnly?: boolean;
  topPanelLeftSpacerWidth?: string | number;
  legendLeftOffset?: string | number;
  isFullscreen?: boolean;
  onToggleFullscreen?: (isFullscreen: boolean) => void;
  isDarkMode?: boolean;
  topBarContent?: React.ReactNode;
  onHiddenNodesChange?: (hiddenNodeIds: Set<string>) => void;
  selectedDate?: string | null;
  onDateChange?: (date: string | null) => void;
  onGraphUpdate?: (nodes: Node[], edges: Edge[]) => void;
  geographicCompanyIds?: ReadonlySet<string> | null;
  onMonitorCompany?: (orgNumber: string, displayName?: string) => Promise<void> | void;
  enableIvoLicenseOverlay?: boolean;
  enableCarEntityList?: boolean;
  enableVehicleExpansion?: boolean;
  enableTenantCases?: boolean;
  enableCompanyComments?: boolean;
  onSaveCompanyComment?: (target: NodeCommentTarget, commentMarkdown: string) => Promise<{
    nodeType: string;
    nodeKey: string;
    commentMarkdown: string;
    createdAt: string;
    updatedAt: string;
  }>;
  onDeleteCompanyComment?: (target: NodeCommentTarget) => Promise<void>;
}

// #region Helper Functions

// ApplyNodeStyling moved to lib/styleProcessor.ts

// getAllNodesWithLabels moved to lib/styleProcessor.ts

// applyEdgeStyling moved to lib/styleProcessor.ts

// getLowestRankRelationship moved to lib/styleProcessor.ts

// calculateBinnedDuration moved to lib/styleProcessor.ts

// getAllEdgesWithLabels moved to lib/styleProcessor.ts

/**
 * Extract unique graph types from both nodes and relationships
 * - Always includes 'all' as a default option
 * - Extracts all properties from GraphType where value is true
 * - Excludes 'badPaths' as specified in requirements
 * 
 * @param nodes Array of nodes from the graph
 * @param relationships Array of edges from the graph
 * @returns Array of unique graph type names as strings
 */
// extractUniqueGraphTypes moved to lib/styleProcessor.ts

// getGraphTypesCompositeKey moved to lib/styleProcessor.ts

// nodeMatchesSearchTerms moved to lib/styleProcessor.ts

// getHighlightedNodes moved to lib/styleProcessor.ts

// #endregion

const OrgGraphVis = forwardRef<OrgGraphVisRef, OrgGraphVisProps>((props, ref) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.resolvedLanguage;
  const {
    nodes,
    relationships,
    orgNumber,
    graphMetaData,
    showFilterButtons,
    getAccessToken,
    hiddenStateStack: hiddenStateStackProp,
    selectedGraphTypes: selectedGraphTypesProp,
    showBadPathsOnly: showBadPathsOnlyProp,
    isDarkMode: isDarkModeProp,
    topPanelLeftSpacerWidth,
    legendLeftOffset,
    isFullscreen: isFullscreenProp,
    onToggleFullscreen,
    topBarContent,
    enableIvoLicenseOverlay = false,
    enableCompanyComments = false,
    enableCarEntityList = false,
    enableVehicleExpansion = false,
    enableTenantCases = false,
  } = props;

  const theme = useTheme();
  // Use prop if provided, otherwise fallback to theme context
  const isDarkMode = isDarkModeProp !== undefined ? isDarkModeProp : theme.palette.mode === 'dark';

  // Get graph configuration based on user's cognito groups
  const graphVizBehaviourOptions = useMemo(
    () => getGraphVizBehaviourOptions(getAccessToken(), isDarkMode),
    [getAccessToken, isDarkMode]
  );

  const [internalIsFullscreen, setInternalIsFullscreen] = useState<boolean>(false);
  const isFullscreen = props.isFullscreen !== undefined ? props.isFullscreen : internalIsFullscreen;
  
  const toggleFullscreen = () => {
    const newState = !isFullscreen;
    if (props.onToggleFullscreen) {
      props.onToggleFullscreen(newState);
    } else {
      setInternalIsFullscreen(newState);
    }
  };

  const containerRef = useRef<HTMLDivElement>(null);

  const [graphNodes, setGraphNodes] = useState<DataSet<Node>>(new DataSet<Node>());
  const [graphEdges, setGraphEdges] = useState<DataSet<Edge>>(new DataSet<Edge>());
  const [expandedEdges, setExpandedEdges] = useState<Edge[]>([]);


  const [graphDataVersion, setGraphDataVersion] = useState<number>(0); // Track when graph data changes
  const [contextMenuParams, setContextMenuParams] = useState<OrgGraphContextMenuProps | null>(null);
  const [showContextMenu, setShowContextMenu] = useState<boolean>(false);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [pathTargetId, setPathTargetId] = useState<string | null>(null);
  const [connectionPath, setConnectionPath] = useState<ConnectionPath | null>(null);
  const [hiddenBranchNotice, setHiddenBranchNotice] = useState<{ count: number; key: string; before: ReturnType<HiddenStateStack['toJSON']>; after: string } | null>(null);
  useEffect(() => {
    setPathTargetId(null);
  }, [selectedNodeId]);
  const [stabilizationProgress, setStabilizationProgress] = useState<number>(0);
  const [stabilizationComplete, setStabilizationComplete] = useState<boolean>(false);
  const networkRef = useRef<Network | null>(null);
  const graphNodesRef = useRef<DataSet<Node>>(graphNodes);

  // Selection state refs
  const selectionRectRef = useRef<{startX: number, startY: number, endX: number, endY: number} | null>(null);
  const isSelectingRef = useRef(false);
  const dragSelectionActiveRef = useRef(false);

  // Handle Shift key for selection mode
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        dragSelectionActiveRef.current = true;
        if (networkRef.current) {
          networkRef.current.setOptions({ interaction: { dragView: false } });
          // Change cursor to crosshair to indicate selection mode
          if (containerRef.current) {
            containerRef.current.style.cursor = 'crosshair';
          }
        }
      }
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        dragSelectionActiveRef.current = false;
        if (networkRef.current) {
          networkRef.current.setOptions({ interaction: { dragView: true } });
          if (containerRef.current) {
            containerRef.current.style.cursor = 'default';
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Overlay Icon Feature
  // Creates number badges that appear in the top-right corner of Company nodes
  // Grey badge for total connections, red badge for connections with debt
  // Moved to lib/canvasUtils.ts

  const isExpanding = useRef<boolean>(false);
  const initialAnimFrameRef = useRef<number | null>(null);

  const [showBadPathsOnly, setShowBadPathsOnly] = useState<boolean>(showBadPathsOnlyProp || false);
  const [activeRelationshipsOnly, setActiveRelationshipsOnly] = useState(false);
  const [selectedGraphTypes, setSelectedGraphTypes] = useState<string[]>(selectedGraphTypesProp || ['all']);
  const [showCompanyComments, setShowCompanyComments] = useState<boolean>(true);
  const shouldShowCompanyComments = enableCompanyComments && showCompanyComments;
  const hasIvoLicenseNodes = useMemo(
    () => nodes.some((node) => node.type === 'Company' && node.properties?.has_ivo_license === true),
    [nodes]
  );
  const shouldShowIvoLicenseOverlay = enableIvoLicenseOverlay && hasIvoLicenseNodes;
  const hasLeasedCarsNodes = useMemo(
    () => [...nodes, ...graphNodes.get()].some((node) => node.type === 'Company' && getCarBadgeLabel(node.properties) !== null),
    [nodes, graphNodes, graphDataVersion]
  );
  // Sync state with props when orgNumber changes or props change
  useEffect(() => {
    if (selectedGraphTypesProp) {
      setSelectedGraphTypes(selectedGraphTypesProp);
    } else {
      setSelectedGraphTypes(['all']);
    }
  }, [orgNumber, selectedGraphTypesProp]);

  useEffect(() => {
    if (showBadPathsOnlyProp !== undefined) {
      setShowBadPathsOnly(showBadPathsOnlyProp);
    } else {
      setShowBadPathsOnly(false);
    }
  }, [orgNumber, showBadPathsOnlyProp]);

  // Initialize with 'all' but will be populated with unique types from the data
  const [availableGraphTypes, setAvailableGraphTypes] = useState<string[]>(['all']);

  const hiddenStateStack = useRef(hiddenStateStackProp || new HiddenStateStack());
  const [userHiddenNodes, setUserHiddenNodes] = useState<Set<string>>(new Set<string>());
  useEffect(() => {
    setHiddenBranchNotice(null);
  }, [orgNumber, selectedGraphTypes]);

  // Notify parent when hidden nodes change
  useEffect(() => {
    if (props.onHiddenNodesChange) {
      props.onHiddenNodesChange(userHiddenNodes);
    }
  }, [userHiddenNodes, props.onHiddenNodesChange]);

  const [physicsSettings, setPhysicsSettings] = useState(
    graphVizBehaviourOptions.physics.forceAtlas2Based
  );

  const [internalSelectedDate, setInternalSelectedDate] = useState<string | null>(null);
  const [streetViewData, setStreetViewData] = useState<{ location: { lat: number, lng: number } | string, address: string } | null>(null);
  const [highlightedNodes, setHighlightedNodes] = useState<Set<string>>(new Set());
  const [searchTerms, setSearchTerms] = useState<SearchTerm[]>([]);
  const [newlyAddedNodes, setNewlyAddedNodes] = useState<Set<string>>(new Set());
  const [newlyAddedEdges, setNewlyAddedEdges] = useState<Set<string>>(new Set());
  const [showNewAdditions, setShowNewAdditions] = useState<boolean>(false);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [normallyExpandedNodes, setNormallyExpandedNodes] = useState<Set<string>>(new Set());
  const [expandedVehicleNodes, setExpandedVehicleNodes] = useState<Set<string>>(new Set());

  // Graph reveal animation state — controls the fade-in after stabilization
  const [graphRevealed, setGraphRevealed] = useState<boolean>(false);
  // Track whether this is an expansion (so we skip the full reveal animation)
  const hasRevealedOnceRef = useRef<boolean>(false);

  // State to track orgNumber for resets
  const [resetOrgNumber, setResetOrgNumber] = useState<string>(orgNumber);
  const isResettingForNewOrg = orgNumber !== resetOrgNumber;

  useEffect(() => {
    graphNodesRef.current = graphNodes;
  }, [graphNodes]);

  useEffect(() => {
    if (!isResettingForNewOrg) {
      return;
    }

    setResetOrgNumber(orgNumber);
    setExpandedEdges([]);
    setExpandedNodes(new Set());
    setNormallyExpandedNodes(new Set());
    setExpandedVehicleNodes(new Set());
    setNewlyAddedNodes(new Set());
    setNewlyAddedEdges(new Set());
    setShowNewAdditions(false);
    setSearchTerms([]);
    setHighlightedNodes(new Set());
    setSelectedNodeId(null);
    setPathTargetId(null);
    setConnectionPath(null);
    setHiddenBranchNotice(null);
    setInternalSelectedDate(null);
    setStabilizationComplete(false);
    setStabilizationProgress(0);
    setGraphRevealed(false);
    hasRevealedOnceRef.current = false;
    viewportStateManager.clearSavedViewport();

    if (initialAnimFrameRef.current) {
      cancelAnimationFrame(initialAnimFrameRef.current);
      initialAnimFrameRef.current = null;
    }
  }, [isResettingForNewOrg, orgNumber]);

  // Calculate min and max dates from all relationships
  const { minDate, maxDate } = React.useMemo(() => {
    let min = '';
    // Max date is always current date
    const max = new Date().toISOString().split('T')[0];
    const sourceNodeId = graphMetaData?.sourceNodeId;
    
    // Combine initial relationships with expanded edges
    // If we are in the middle of a reset (orgNumber changed but state not updated yet),
    // we should treat expandedEdges as empty to avoid mixing data from different graphs.
    const effectiveExpandedEdges = isResettingForNewOrg ? [] : expandedEdges;
    const allRelationships = [...relationships, ...effectiveExpandedEdges];

    if (sourceNodeId) {
      allRelationships.forEach(edge => {
        // Calculate min date ONLY from Source Node relationships
        const isSourceEdge = edge.from === sourceNodeId || edge.to === sourceNodeId;
        if (isSourceEdge) {
          if (edge.properties.start_date) {
            if (!min || edge.properties.start_date < min) min = edge.properties.start_date;
          }
          if (edge.properties.end_date) {
            if (!min || edge.properties.end_date < min) min = edge.properties.end_date;
          }
        }
      });
    }
    
    // If no min date found, return nulls
    if (!min) return { minDate: null, maxDate: null };
    
    return { minDate: min, maxDate: max };
  }, [relationships, expandedEdges, graphMetaData, isResettingForNewOrg]);

  const selectedDate = props.selectedDate !== undefined ? props.selectedDate : internalSelectedDate;
  const setSelectedDate = (date: string | null) => {
    if (props.onDateChange) {
      props.onDateChange(date);
    } else {
      setInternalSelectedDate(date);
    }
  };

  // Apply the same date and activity filters to initial and expanded relationships.
  const filteredRelationships = React.useMemo(() => {
    // If we are in the middle of a reset (orgNumber changed but state not updated yet),
    // we should treat expandedEdges as empty to avoid mixing data from different graphs.
    const effectiveExpandedEdges = isResettingForNewOrg ? [] : expandedEdges;

    // Deduplicate: prefer relationships (props) over expandedEdges (local state)
    const relationshipIds = new Set(relationships.map(r => String(r.id)));
    const uniqueExpandedEdges = effectiveExpandedEdges.filter(e => !relationshipIds.has(String(e.id)));
    
    const allRelationships = [...relationships, ...uniqueExpandedEdges];
    
    return filterRelationshipsByDate(allRelationships, selectedDate, activeRelationshipsOnly);
  }, [relationships, expandedEdges, selectedDate, activeRelationshipsOnly, isResettingForNewOrg]);

  // Helper function to detect if nodes have saved positions (indicating case load)
  const hasNodesWithPositions = (nodes: Node[]): boolean => {
    return nodes.some(node => 
      (node as any).x !== undefined && (node as any).y !== undefined &&
      (node as any).x !== null && (node as any).y !== null
    );
  };

  const getCurrentNodePositions = useCallback(
    (nodeIds?: Array<string | number>): Record<string, { x: number; y: number }> => {
      if (!networkRef.current) {
        return {};
      }

      const ids = nodeIds ?? (graphNodesRef.current.getIds() as Array<string | number>);
      if (ids.length === 0) {
        return {};
      }

      return networkRef.current.getPositions(ids);
    },
    []
  );

  const isActiveNetworkInstance = useCallback(
    (network: Network | null | undefined): network is Network =>
      !!network && networkRef.current === network,
    []
  );

  // Function to get the current graph state using useCallback to prevent stale closures
  const getGraphState = useCallback((): OrgGraphState | null => {
    if (!networkRef.current) {
      return null;
    }

    // Ensure we're getting the absolute latest positions
    networkRef.current.storePositions();
    const positions = getCurrentNodePositions();

    // Get fresh data directly from DataSets
    const currentNodes = graphNodes.get();
    const currentEdges = graphEdges.get();
    
    console.log("Fresh GraphNodes in getGraphState:", currentNodes);

    const nodes = currentNodes.map((node) => {
      return {
        ...node,
        x: positions[node.id]?.x ?? (node as any).x ?? 0,
        y: positions[node.id]?.y ?? (node as any).y ?? 0,
        fixed: true
      };
    });

    return {
      nodes: nodes,
      relationships: currentEdges,
      hiddenStateStack: hiddenStateStack.current,
      selectedGraphTypes: selectedGraphTypes,
      showBadPathsOnly: showBadPathsOnly,
      orgNumber: orgNumber,
      contextId: getContextId()
    };
  }, [graphNodes, graphEdges, hiddenStateStack, selectedGraphTypes, showBadPathsOnly, orgNumber, getCurrentNodePositions]);

  // Function to calculate max safe scale
  const getMaxScale = useCallback(() => {
     if (!containerRef.current) return 36;
     
      const canvas = containerRef.current.querySelector('canvas');
      const sourceElement = containerRef.current.querySelector('.vis-network') || containerRef.current;
      
      const pixelRatio = window.devicePixelRatio || 1;
      let sourceWidth: number = 0, sourceHeight: number = 0;
      
      if (canvas) {
        sourceWidth = canvas.width / pixelRatio;
        sourceHeight = canvas.height / pixelRatio;
      } else {
        const rect = sourceElement.getBoundingClientRect();
        sourceWidth = rect.width;
        sourceHeight = rect.height;
      }

      if (sourceWidth === 0 || sourceHeight === 0) return 36;
      
      const MAX_CANVAS_DIM = 16000;
      const MAX_PIXELS = 36000000;
      
      const maxScaleW = MAX_CANVAS_DIM * pixelRatio / sourceWidth;
      const maxScaleH = MAX_CANVAS_DIM * pixelRatio / sourceHeight;
      const maxScaleA = pixelRatio * Math.sqrt(MAX_PIXELS / (sourceWidth * sourceHeight));
      
      return Math.floor(Math.min(maxScaleW, maxScaleH, maxScaleA));
  }, []);

  // Function to export the graph as PNG
  const exportGraphAsPNG = useCallback((fileName?: string, scale: number = 36) => {
    return new Promise<void>((resolve, reject) => {
    if (!networkRef.current || !containerRef.current) {
      console.error('Network or container not initialized');
      reject('Network or container not initialized');
      return;
    }

    try {
      const network = networkRef.current;
      
      // Find the canvas to get the exact aspect ratio and resolution
      const canvas = containerRef.current.querySelector('canvas');
      const sourceElement = containerRef.current.querySelector('.vis-network') || containerRef.current;
      
      // Use canvas dimensions if available, otherwise fallback to container rect
      const pixelRatio = window.devicePixelRatio || 1;
      let sourceWidth, sourceHeight;
      
      if (canvas) {
        sourceWidth = canvas.width / pixelRatio;
        sourceHeight = canvas.height / pixelRatio;
      } else {
        const rect = sourceElement.getBoundingClientRect();
        sourceWidth = rect.width;
        sourceHeight = rect.height;
      }
      
      // 1. Get current view parameters
      const currentScale = network.getScale();
      const currentPosition = network.getViewPosition();
      const currentPositions = getCurrentNodePositions();
      
      // 2. Define high-res scale factor
      // Use a fixed target scale relative to CSS pixels, but adjust for device pixel ratio
      // to prevent canvas size explosion on high-DPI or zoomed screens
      // Increased from 4 to 8 to improve readability for large networks
      const targetScale = scale; 
      let scaleFactor = targetScale / pixelRatio;
      
      // Safety limits to prevent browser canvas crashes
      // Lowered to 12000 to be very safe from browser limits
      // Cap total pixels to 36MP (6k x 6k approx) to prevent OOM
      const MAX_CANVAS_DIM = 12000; 
      const MAX_PIXELS = 36000000;

      let width = sourceWidth * scaleFactor;
      let height = sourceHeight * scaleFactor;
      
      // Downscale if limits are exceeded
      if (width > MAX_CANVAS_DIM || height > MAX_CANVAS_DIM || (width * height) > MAX_PIXELS) {
         console.warn(`⚠️ Export resolution capped by browser limits. Requested: ${Math.round(width)}x${Math.round(height)}`);
         const ratioW = width > MAX_CANVAS_DIM ? MAX_CANVAS_DIM / width : 1;
         const ratioH = height > MAX_CANVAS_DIM ? MAX_CANVAS_DIM / height : 1;
         const ratioA = (width * height) > MAX_PIXELS ? Math.sqrt(MAX_PIXELS / (width * height)) : 1;
         
         const adjustment = Math.min(ratioW, ratioH, ratioA);
         scaleFactor *= adjustment;
         width *= adjustment;
         height *= adjustment;

         console.warn(`📉 Adjusted export resolution to: ${Math.round(width)}x${Math.round(height)}`);
      }
      
      // 3. Create a temporary hidden container
      const exportContainer = document.createElement('div');
      exportContainer.style.position = 'absolute';
      exportContainer.style.top = '0';
      exportContainer.style.left = '0';
      exportContainer.style.width = `${width}px`;
      exportContainer.style.height = `${height}px`;
      exportContainer.style.zIndex = '-9999';
      exportContainer.style.opacity = '0';
      exportContainer.style.pointerEvents = 'none';
      exportContainer.style.backgroundColor = isDarkMode ? '#000000' : '#ffffff';
      document.body.appendChild(exportContainer);
      
      // 4. Prepare data with exact positions
      const currentNodes = graphNodes.get();
      const exportNodes = currentNodes.map(node => ({
        ...node,
        x: currentPositions[node.id]?.x,
        y: currentPositions[node.id]?.y,
        fixed: true // Fix positions
      }));
      
      const currentEdges = graphEdges.get();
      
      const exportData = {
        nodes: new DataSet(exportNodes),
        edges: new DataSet(currentEdges)
      };
      
      // 5. Create options for the export network
      const exportOptions = {
        ...graphVizBehaviourOptions,
        physics: { enabled: false },
        interaction: { 
            dragNodes: false, 
            zoomView: false, 
            dragView: false 
        },
        layout: { improvedLayout: false },
        autoResize: false,
        width: `${width}px`,
        height: `${height}px`
      };
      
      // 6. Initialize the export network
      const exportNetwork = new Network(exportContainer, exportData, exportOptions);
      
      // CRITICAL FIX: Override global CSS that forces .vis-network to 800px height
      const networkWrapper = exportContainer.querySelector('.vis-network') as HTMLElement;
      if (networkWrapper) {
        networkWrapper.style.setProperty('height', `${height}px`, 'important');
        networkWrapper.style.setProperty('width', `${width}px`, 'important');
      }
      
      exportNetwork.setSize(`${width}px`, `${height}px`);
      
      // 7. Set the view to match the original, scaled up
      setTimeout(() => {
        // Apply the view transformation
        exportNetwork.moveTo({
          position: currentPosition,
          scale: currentScale * scaleFactor,
          offset: { x: 0, y: 0 },
          animation: false
        });
        
        // Force redraw and export
        exportNetwork.once('afterDrawing', (ctx) => {
          // Fill background with correct color
          ctx.save();
          ctx.setTransform(1, 0, 0, 1, 0, 0); // Reset to identity matrix (pixels)
          ctx.globalCompositeOperation = 'destination-over';
          ctx.fillStyle = isDarkMode ? '#000000' : '#ffffff';
          ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
          ctx.restore();

          // Draw overlay icons
          drawOverlayIcons(
            ctx,
            exportNetwork,
            graphNodes,
            expandedNodes,
            graphMetaData.sourceNodeId,
            shouldShowCompanyComments,
            shouldShowIvoLicenseOverlay,
          );
          
          // Get the canvas and export
          const canvas = exportContainer.getElementsByTagName('canvas')[0];
          
          // Filename
          const now = new Date();
          const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
          const finalFilename = fileName || `${orgNumber || 'graph'}_${dateStr}.png`;
          
          canvas.toBlob((blob) => {
            if (blob) {
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = finalFilename;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
              console.log(`✅ Graph exported as ${finalFilename}`);
              resolve();
            } else {
              reject('Failed to create blob');
            }
            
            // Cleanup
            exportNetwork.destroy();
            document.body.removeChild(exportContainer);
          }, 'image/png', 1.0);
        });
        
        // Trigger the redraw that will fire the event above
        exportNetwork.redraw();
      }, 100);

    } catch (error) {
      console.error('Error exporting graph as PNG:', error);
      reject(error);
    }
   });
  }, [orgNumber, graphNodes, graphEdges, graphVizBehaviourOptions, isDarkMode, getCurrentNodePositions, shouldShowCompanyComments, shouldShowIvoLicenseOverlay]);

  // Function to export the graph as SVG (vector format — infinite zoom, sharp text)
  const exportGraphAsSVG = useCallback((fileName?: string) => {
    return new Promise<void>((resolve, reject) => {
      if (!networkRef.current || !containerRef.current) {
        console.error('Network or container not initialized');
        reject('Network or container not initialized');
        return;
      }

      try {
        const network = networkRef.current;
        network.storePositions();
        const positions = network.getPositions();

        // Get visible nodes and edges
        const visibleNodes = graphNodes.get().filter((n: any) => !n.hidden);
        const visibleEdges = graphEdges.get().filter((e: any) => !e.hidden);

        if (visibleNodes.length === 0) {
          reject('No visible nodes to export');
          return;
        }

        // Calculate bounding box of all visible nodes (including label space)
        let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
        visibleNodes.forEach((node: any) => {
          const pos = positions[node.id];
          if (!pos) return;
          const size = node.size || 25;
          const labelLines = (node.label || '').split('\n');
          const fontSize = node.font?.size || 14;
          const labelHeight = labelLines.length * fontSize * 1.3;
          // Estimate label width
          const maxLabelWidth = Math.max(...labelLines.map((l: string) => l.length)) * fontSize * 0.6;
          const halfWidth = Math.max(size, maxLabelWidth / 2);
          minX = Math.min(minX, pos.x - halfWidth);
          maxX = Math.max(maxX, pos.x + halfWidth);
          minY = Math.min(minY, pos.y - size);
          maxY = Math.max(maxY, pos.y + size + labelHeight + 8);

          const badges = getNodeBadges(node, {
            isExpanded: expandedNodes.has(node.id),
            isSource: Boolean(graphMetaData?.sourceNodeId && node.id === graphMetaData.sourceNodeId),
            showCompanyComments: shouldShowCompanyComments,
            showIvoLicenses: shouldShowIvoLicenseOverlay,
          });
          const badgeLayout = layoutBadges(badges);

          if (badgeLayout.length > 0) {
            maxX = Math.max(maxX, pos.x + 10 + Math.max(...badgeLayout.map((badge) => badge.x + badge.width)));
            minY = Math.min(minY, pos.y - 30);
            maxY = Math.max(maxY, pos.y - 30 + Math.max(...badgeLayout.map((badge) => badge.y + badge.height)));
          }
        });

        const padding = 80;
        minX -= padding; minY -= padding;
        maxX += padding; maxY += padding;
        const svgWidth = maxX - minX;
        const svgHeight = maxY - minY;

        const escapeXml = (str: string) => str
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&apos;');

        const bgColor = isDarkMode ? '#000000' : '#ffffff';
        const parts: string[] = [];

        parts.push(`<?xml version="1.0" encoding="UTF-8"?>`);
        parts.push(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="${minX} ${minY} ${svgWidth} ${svgHeight}" width="${svgWidth}" height="${svgHeight}">`);

        // Defs: font import for FA icons + edge label background filter
        parts.push(`<defs>`);
        parts.push(`<style>`);
        parts.push(`@import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css');`);
        parts.push(`text { font-family: Arial, Helvetica, sans-serif; }`);
        parts.push(`.fa-icon { font-family: 'Font Awesome 6 Free', 'Font Awesome 5 Free', 'FontAwesome'; font-weight: 900; }`);
        parts.push(`</style>`);
        parts.push(`</defs>`);

        // Background
        parts.push(`<rect x="${minX}" y="${minY}" width="${svgWidth}" height="${svgHeight}" fill="${bgColor}"/>`);

        // ── Edges ──
        parts.push(`<g class="edges">`);
        visibleEdges.forEach((edge: any) => {
          const fromPos = positions[edge.from];
          const toPos = positions[edge.to];
          if (!fromPos || !toPos) return;

          const edgeColor = edge.color?.color || (isDarkMode ? '#bdc3c7' : '#000000');
          // Scale edge width for SVG visibility (canvas widths like 0.1 are invisible in SVG)
          const edgeWidth = Math.max((edge.width || 0.1) * 3, 0.5);
          const dashes = edge.dashes;

          // Compute curve for curvedCW edges
          const roundness = edge.smooth?.roundness || 0;
          const dx = toPos.x - fromPos.x;
          const dy = toPos.y - fromPos.y;
          const dist = Math.sqrt(dx * dx + dy * dy) || 1;

          let pathD: string;
          let labelX: number, labelY: number;

          if (roundness === 0 || dist < 1) {
            pathD = `M ${fromPos.x} ${fromPos.y} L ${toPos.x} ${toPos.y}`;
            labelX = (fromPos.x + toPos.x) / 2;
            labelY = (fromPos.y + toPos.y) / 2;
          } else {
            // Perpendicular offset (clockwise) for quadratic Bézier control point
            const midX = (fromPos.x + toPos.x) / 2;
            const midY = (fromPos.y + toPos.y) / 2;
            const offset = roundness * dist;
            const nx = dy / dist;   // perpendicular x (CW)
            const ny = -dx / dist;  // perpendicular y (CW)
            const cx = midX + nx * offset;
            const cy = midY + ny * offset;
            pathD = `M ${fromPos.x} ${fromPos.y} Q ${cx} ${cy} ${toPos.x} ${toPos.y}`;
            // Label at curve midpoint (t=0.5 on quadratic Bézier)
            labelX = 0.25 * fromPos.x + 0.5 * cx + 0.25 * toPos.x;
            labelY = 0.25 * fromPos.y + 0.5 * cy + 0.25 * toPos.y;
          }

          let strokeDasharray = '';
          if (dashes === true) {
            strokeDasharray = ' stroke-dasharray="8 4"';
          } else if (Array.isArray(dashes)) {
            strokeDasharray = ` stroke-dasharray="${dashes.join(' ')}"`;
          }

          parts.push(`<path d="${pathD}" stroke="${edgeColor}" stroke-width="${edgeWidth}" fill="none"${strokeDasharray}/>`);

          // Edge label
          if (edge.label && edge.label.trim()) {
            const edgeFontSize = edge.font?.size || 12;
            const edgeFontColor = edge.font?.color || (isDarkMode ? '#ecf0f1' : '#000000');
            const lines = edge.label.split('\n').filter((l: string) => l.trim());
            const totalHeight = lines.length * edgeFontSize * 1.2;
            const startLabelY = labelY - totalHeight / 2 + edgeFontSize * 0.4;

            lines.forEach((line: string, i: number) => {
              const ly = startLabelY + i * edgeFontSize * 1.2;
              // Label background for readability
              const approxWidth = line.length * edgeFontSize * 0.55;
              parts.push(`<rect x="${labelX - approxWidth / 2 - 2}" y="${ly - edgeFontSize * 0.8}" width="${approxWidth + 4}" height="${edgeFontSize * 1.2}" fill="${bgColor}" opacity="0.85" rx="2" ry="2"/>`);
              parts.push(`<text x="${labelX}" y="${ly}" text-anchor="middle" font-size="${edgeFontSize}" fill="${edgeFontColor}">${escapeXml(line)}</text>`);
            });
          }
        });
        parts.push(`</g>`);

        // ── Nodes ──
        parts.push(`<g class="nodes">`);
        visibleNodes.forEach((node: any) => {
          const pos = positions[node.id];
          if (!pos) return;

          const size = node.size || 25;
          const nodeColor = typeof node.color === 'string' ? node.color : (node.color?.background || '#97C2FC');
          const borderColor = typeof node.color === 'string' ? node.color : (node.color?.border || nodeColor);
          const borderWidth = node.borderWidth || 2;

          // Node circle
          parts.push(`<circle cx="${pos.x}" cy="${pos.y}" r="${size}" fill="${nodeColor}" stroke="${borderColor}" stroke-width="${borderWidth}"/>`);

          // Font Awesome icon inside node
          if (node.icon?.code) {
            const iconColor = node.icon.color || '#ffffff';
            const iconFontSize = node.icon.size ? node.icon.size * 0.9 : 18;
            parts.push(`<text class="fa-icon" x="${pos.x}" y="${pos.y}" text-anchor="middle" dominant-baseline="central" font-size="${iconFontSize}" fill="${iconColor}">${node.icon.code}</text>`);
          }

          // Label below node
          if (node.label) {
            const fontSize = node.font?.size || 14;
            const fontColor = node.font?.color || (isDarkMode ? '#ecf0f1' : '#000000');
            const fontBg = node.font?.background;
            const lines = node.label.split('\n');
            const startY = pos.y + size + fontSize + 4;

            lines.forEach((line: string, i: number) => {
              const y = startY + i * fontSize * 1.3;
              if (!line) return; // Skip empty lines but keep vertical space

              // Background highlight for search/new nodes
              if (fontBg && fontBg !== 'transparent') {
                const textWidth = line.length * fontSize * 0.6;
                parts.push(`<rect x="${pos.x - textWidth / 2 - 2}" y="${y - fontSize + 2}" width="${textWidth + 4}" height="${fontSize + 4}" fill="${fontBg}" rx="2" ry="2"/>`);
              }

              parts.push(`<text x="${pos.x}" y="${y}" text-anchor="middle" font-size="${fontSize}" fill="${fontColor}">${escapeXml(line)}</text>`);
            });
          }

          // Overlay badges
          const badges = getNodeBadges(node, {
            isExpanded: expandedNodes.has(node.id),
            isSource: Boolean(graphMetaData?.sourceNodeId && node.id === graphMetaData.sourceNodeId),
            showCompanyComments: shouldShowCompanyComments,
            showIvoLicenses: shouldShowIvoLicenseOverlay,
          });

          if (badges.length > 0) {
            const badgeStartX = pos.x + 10;
            const badgeStartY = pos.y - 30;

            layoutBadges(badges).forEach((badge) => {
              const x = badgeStartX + badge.x;
              const y = badgeStartY + badge.y;
              const color = getBadgeColor(badge.type);

              if (badge.type === 'employmentSupport') {
                parts.push(`<circle cx="${x + badge.width / 2}" cy="${y + badge.height / 2}" r="${badge.height / 2}" fill="${color}"/>`);
                parts.push(`<text class="fa-icon" x="${x + badge.width / 2}" y="${y + badge.height / 2}" text-anchor="middle" dominant-baseline="central" font-size="12" fill="#ffffff">${badge.value}</text>`);
              } else if (badge.type === 'leasedCars') {
                parts.push(`<rect x="${x}" y="${y}" width="${badge.width}" height="${badge.height}" rx="${badge.height / 2}" ry="${badge.height / 2}" fill="${color}" stroke="#ffffff" stroke-width="2"/>`);
                parts.push(`<text class="fa-icon" x="${x + 11}" y="${y + badge.height / 2}" text-anchor="middle" dominant-baseline="central" font-size="10" fill="#ffffff">${LEASED_CARS_ICON}</text>`);
                parts.push(`<text x="${x + 12 + (badge.width - 12) / 2}" y="${y + badge.height / 2}" text-anchor="middle" dominant-baseline="central" font-size="11" font-weight="bold" fill="#ffffff">${escapeXml(badge.value.toString())}</text>`);
              } else if (badge.type === 'tenantCase') {
                parts.push(`<rect x="${x}" y="${y}" width="${badge.width}" height="${badge.height}" rx="${badge.height / 2}" ry="${badge.height / 2}" fill="${color}" stroke="#ffffff" stroke-width="2"/>`);
                parts.push(`<path d="M ${x + 6.5} ${y + 5.5} H ${x + 11.5} L ${x + 15.5} ${y + 9.5} V ${y + 18.5} H ${x + 6.5} Z M ${x + 11.5} ${y + 5.5} V ${y + 9.5} H ${x + 15.5}" fill="none" stroke="#ffffff" stroke-width="1.5" stroke-linejoin="round"/>`);
                parts.push(`<text x="${x + 17 + (badge.width - 17) / 2}" y="${y + badge.height / 2}" text-anchor="middle" dominant-baseline="central" font-size="11" font-weight="bold" fill="#ffffff">${escapeXml(badge.value.toString())}</text>`);
              } else {
                const badgeR = badge.height / 2;
                const bx = x + badgeR;
                const by = y + badgeR;
                parts.push(`<circle cx="${bx}" cy="${by}" r="${badgeR}" fill="${color}"/>`);
                parts.push(`<text x="${bx}" y="${by}" text-anchor="middle" dominant-baseline="central" font-size="11" font-weight="bold" fill="#ffffff">${escapeXml(badge.value.toString())}</text>`);
              }
            });
          }
        });
        parts.push(`</g>`);

        parts.push(`</svg>`);

        // Download
        const svgString = parts.join('\n');
        const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const now = new Date();
        const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
        link.download = fileName || `${orgNumber || 'graph'}_${dateStr}.svg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        console.log(`✅ Graph exported as SVG`);
        resolve();
      } catch (error) {
        console.error('Error exporting graph as SVG:', error);
        reject(error);
      }
    });
  }, [orgNumber, graphNodes, graphEdges, isDarkMode, expandedNodes, graphMetaData, shouldShowCompanyComments, shouldShowIvoLicenseOverlay]);

  // Label settings state (must be before useImperativeHandle so they can be referenced)
  const [nodeLabelSettings, setNodeLabelSettings] = useState<NodeLabelSettings>(loadNodeLabelSettings);
  const [edgeLabelSettings, setEdgeLabelSettings] = useState<EdgeLabelSettings>(loadEdgeLabelSettings);

  const handleNodeLabelSettingsChange = useCallback((settings: NodeLabelSettings) => {
    setNodeLabelSettings(settings);
    saveNodeLabelSettings(settings);
  }, []);

  const handleEdgeLabelSettingsChange = useCallback((settings: EdgeLabelSettings) => {
    setEdgeLabelSettings(settings);
    saveEdgeLabelSettings(settings);
  }, []);

  // Expose methods to parent components via ref
  useImperativeHandle(ref, () => ({
    getGraphState,
    exportGraphAsPNG,
    exportGraphAsSVG,
    getMaxScale,
    getNodeLabelSettings: () => nodeLabelSettings,
    setNodeLabelSettings: (settings: NodeLabelSettings) => {
      handleNodeLabelSettingsChange(settings);
    },
    getEdgeLabelSettings: () => edgeLabelSettings,
    setEdgeLabelSettings: (settings: EdgeLabelSettings) => {
      handleEdgeLabelSettingsChange(settings);
    },
  }), [getGraphState, exportGraphAsPNG, exportGraphAsSVG, getMaxScale, nodeLabelSettings, edgeLabelSettings, handleNodeLabelSettingsChange, handleEdgeLabelSettingsChange]);

  const updateCompanyCommentNode = useCallback((
    nodeId: string,
    updateProperties: (properties: Record<string, any>) => void,
  ) => {
    const currentNode = graphNodes.get(nodeId);
    if (!currentNode) {
      return;
    }

    const updatedProperties = { ...(currentNode.properties || {}) };
    updateProperties(updatedProperties);

    const updatedNode = {
      ...currentNode,
      properties: updatedProperties,
    };

    ApplyNodeStyling(
      updatedNode,
      orgNumber,
      graphMetaData,
      highlightedNodes,
      newlyAddedNodes,
      showNewAdditions,
      expandedNodes,
      nodeLabelSettings,
      isDarkMode,
      shouldShowCompanyComments,
      currentLanguage,
    );

    graphNodes.update(updatedNode);
    setGraphDataVersion((previous) => previous + 1);
    networkRef.current?.redraw();
    setContextMenuParams((previous) => (
      previous && previous.node.id === nodeId
        ? { ...previous, node: updatedNode }
        : previous
    ));
  }, [
    expandedNodes,
    graphMetaData,
    graphNodes,
    highlightedNodes,
    isDarkMode,
    newlyAddedNodes,
    nodeLabelSettings,
    orgNumber,
    shouldShowCompanyComments,
    showNewAdditions,
  ]);

  const handleWatchlistChange = (node: Node, isOnWatchList: boolean) => {
    graphNodes.update({ id: node.id, isOnWatchList });
    setGraphDataVersion(previous => previous + 1);
    setContextMenuParams(previous => previous?.node.id === node.id
      ? { ...previous, node: { ...previous.node, isOnWatchList } } : previous);
  };

  const handleSaveCompanyComment = useCallback(async (node: Node, commentMarkdown: string) => {
    const commentTarget = getNodeCommentTarget(node);
    if (!commentTarget || !props.onSaveCompanyComment) {
      return;
    }

    const result = await props.onSaveCompanyComment(commentTarget, commentMarkdown);
    updateCompanyCommentNode(node.id, (properties) => {
      properties.has_comment = true;
      properties.comment_markdown = result.commentMarkdown;
      properties.comment_updated_at = result.updatedAt;
    });
  }, [props.onSaveCompanyComment, updateCompanyCommentNode]);

  const handleDeleteCompanyComment = useCallback(async (node: Node) => {
    const commentTarget = getNodeCommentTarget(node);
    if (!commentTarget || !props.onDeleteCompanyComment) {
      return;
    }

    await props.onDeleteCompanyComment(commentTarget);
    updateCompanyCommentNode(node.id, (properties) => {
      properties.has_comment = false;
      delete properties.comment_markdown;
      delete properties.comment_updated_at;
    });
  }, [props.onDeleteCompanyComment, updateCompanyCommentNode]);

  const [isSearchPanelMinimized, setIsSearchPanelMinimized] = useState<boolean>(true);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState<boolean>(enableCarEntityList);
  const [isSummaryVisible, setIsSummaryVisible] = useState<boolean>(true);

  // Node expansion state
  const [isExpansionLoading, setIsExpansionLoading] = useState<boolean>(false);
  const [expansionMessage, setExpansionMessage] = useState<string>('');

  // Data fetching state (from right-click to API response)
  const [isFetchingData, setIsFetchingData] = useState<boolean>(false);

  // Method to unlock all nodes (remove fixed positions) to make them draggable
  const unlockAllNodes = useCallback(() => {
    if (!networkRef.current) return;

    const currentPositions = getCurrentNodePositions();
    const allNodes = graphNodes.get();
    
    console.log('🔓 Unlocking all nodes for dragging...');
    
    // Update nodes to remove fixed properties while preserving positions
    const nodesToUpdate = allNodes.map(node => {
      const currentPos = currentPositions[node.id];
      return {
        ...node,
        // Keep current positions but remove fixed constraints
        x: currentPos?.x ?? (node as any).x,
        y: currentPos?.y ?? (node as any).y,
        fixed: undefined // Remove all fixed constraints
      };
    });

    if (nodesToUpdate.length > 0) {
      // Update all nodes at once to remove fixed properties while keeping positions
      graphNodes.update(nodesToUpdate);
      
      // Ensure physics stays disabled to prevent any movement
      if (networkRef.current) {
        networkRef.current.setOptions({ physics: { enabled: false } });
      }
      
      console.log('🔓 All nodes are now draggable');
    }
  }, [graphNodes, getCurrentNodePositions]);

  // Method to update a node's position when manually dragged
  const updateNodePosition = (nodeId: string, newPosition: { x: number; y: number }) => {
    const node = graphNodes.get(nodeId);
    if (!node) return;

    const updatedNode = {
      ...node,
      x: newPosition.x,
      y: newPosition.y
    };

    graphNodes.update(updatedNode);
  };

  // ── Expansion slide animation (smooth fly-in after stabilization) ──────────
  const { startSlideAnimation, cancelAnimation: cancelSlideAnimation } =
    useExpansionSlideAnimation(networkRef, graphNodes);

  useEffect(() => {
    return () => {
      if (initialAnimFrameRef.current) {
        cancelAnimationFrame(initialAnimFrameRef.current);
        initialAnimFrameRef.current = null;
      }
      cancelSlideAnimation();
    };
  }, [cancelSlideAnimation]);

  // ── Initial load: animate nodes from center → final positions ──────────────
  // Completely independent from expansion animation — uses its own rAF loop.

  const handleInitialStabilizationDone = useCallback(() => {
    // Only run once per graph load — skip if already revealed (e.g. during expansion)
    if (hasRevealedOnceRef.current) return;

    const network = networkRef.current;
    if (!network) return;

    network.storePositions();
    const finalPositions = getCurrentNodePositions();
    const allNodes = graphNodes.get();

    if (allNodes.length === 0) return;

    // Use the source node's final position as the center origin
    const sourceId = String(graphMetaData?.sourceNodeId ?? orgNumber);
    const centerPos = finalPositions[sourceId] || { x: 0, y: 0 };

    // Build list of nodes with from/to positions
    const nodes: { id: string; fromX: number; fromY: number; toX: number; toY: number }[] = [];
    allNodes.forEach((node: any) => {
      const id = String(node.id);
      const fp = finalPositions[id];
      if (!fp) return;
      nodes.push({ id, fromX: centerPos.x, fromY: centerPos.y, toX: fp.x, toY: fp.y });
    });

    if (nodes.length === 0) return;

    // Snap all nodes to center and make them transparent
    graphNodes.update(nodes.map(n => ({ id: n.id, x: n.fromX, y: n.fromY, fixed: true, opacity: 0 } as any)));

    // Make wrapper visible immediately so animation is seen
    setGraphRevealed(true);
    hasRevealedOnceRef.current = true;

    const DURATION = 2000;
    const easeInOutCubic = (t: number) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    const startTime = Date.now();

    const tick = () => {
      if (!isActiveNetworkInstance(network)) {
        initialAnimFrameRef.current = null;
        return;
      }

      const elapsed = Date.now() - startTime;
      const raw = Math.min(1, elapsed / DURATION);
      const eased = easeInOutCubic(raw);

      graphNodes.update(nodes.map(n => ({
        id: n.id,
        x: n.fromX + (n.toX - n.fromX) * eased,
        y: n.fromY + (n.toY - n.fromY) * eased,
        opacity: eased,
      } as any)));

      network.redraw();

      if (raw < 1) {
        initialAnimFrameRef.current = requestAnimationFrame(tick);
      } else {
        // Done — unlock nodes at final positions
        graphNodes.update(nodes.map(n => ({ id: n.id, x: n.toX, y: n.toY, fixed: false, opacity: 1 } as any)));
        initialAnimFrameRef.current = null;
        console.log('✨ Initial load slide animation complete');
      }
    };

    console.log(`✨ Starting initial load slide animation for ${nodes.length} node(s)`);
    initialAnimFrameRef.current = requestAnimationFrame(tick);
  }, [graphNodes, graphMetaData, orgNumber, getCurrentNodePositions, isActiveNetworkInstance]);

  const { handleExpandNode } = useGraphExpansion({
    setExpandedNodes,
    setIsExpansionLoading,
    setExpansionMessage,
    graphNodes,
    graphEdges,
    isExpanding,
    networkRef,
    graphMetaData,
    graphVizBehaviourOptions,
    physicsSettings,
    setGraphDataVersion,
    unlockAllNodes,
    setNewlyAddedNodes,
    setNewlyAddedEdges,
    setShowNewAdditions,
    getAccessToken,
    orgNumber,
    highlightedNodes,
    expandedNodes,
    nodeLabelSettings,
    edgeLabelSettings,
    setStabilizationComplete,
    onSlideAnimation: startSlideAnimation,
    isDarkMode, // Pass isDarkMode to useGraphExpansion
    showCompanyComments: shouldShowCompanyComments,
    onExpansionComplete: (newNodes, newEdges) => {
      // Filter newEdges to remove duplicates and bad edges before notifying parent or updating state
      const knownEdgeIds = new Set<string>();
      // Map of "from_to" -> boolean (true if we have an edge with start_date)
      const existingConnections = new Map<string, boolean>();

      const registerEdge = (e: Edge) => {
        if (e.id != null) knownEdgeIds.add(String(e.id));
        const key = `${String(e.from)}_${String(e.to)}`;
        const hasDate = !!(e.properties && e.properties.start_date);
        if (hasDate) {
          existingConnections.set(key, true);
        } else if (!existingConnections.has(key)) {
          existingConnections.set(key, false);
        }
      };

      relationships.forEach(registerEdge);
      expandedEdges.forEach(registerEdge);

      const uniqueNewEdges = newEdges.filter(e => {
        if (e.id == null) return false;
        const idStr = String(e.id);
        
        if (knownEdgeIds.has(idStr)) return false;
        
        // Heuristic: If new edge lacks start_date, but we already have a dated connection
        // between these nodes, discard the new edge as a likely lower-quality duplicate.
        const key = `${String(e.from)}_${String(e.to)}`;
        const newHasDate = !!(e.properties && e.properties.start_date);
        
        if (!newHasDate && existingConnections.get(key) === true) {
          return false;
        }
        
        knownEdgeIds.add(idStr);
        return true;
      });

      // Notify parent about graph update with FILTERED edges
      if (props.onGraphUpdate) {
        props.onGraphUpdate(newNodes, uniqueNewEdges);
      }

      // Update local state with new edges to ensure they persist through time slider changes
      setExpandedEdges(prev => [...prev, ...uniqueNewEdges]);
    }
  });

  const handleNormalExpansion = useCallback((newNodes: Node[], newEdges: Edge[], expansionNodeId: string) => {
    setNormallyExpandedNodes(prev => new Set(prev).add(expansionNodeId));
    handleExpandNode(newNodes, newEdges, expansionNodeId);
  }, [handleExpandNode]);

  const handleVehicleExpansion = useCallback((newNodes: Node[], newEdges: Edge[], expansionNodeId: string) => {
    setExpandedVehicleNodes(prev => new Set(prev).add(expansionNodeId));
    handleExpandNode(newNodes, newEdges, expansionNodeId);
  }, [handleExpandNode]);


  // Add this function to handle fullscreen toggling
  // Note: toggleFullscreen is already defined above to handle controlled/uncontrolled state
  // We just need to handle the side effects (resizing) here
  useEffect(() => {
    if (isFullscreen) {
      // Enter fullscreen mode (no browser fullscreen, just our own fullscreen overlay)
      
      // Add a delay to let the DOM update, then force resize
      setTimeout(() => {
        if (networkRef.current) {
          // Force the network to resize - the container now includes the top panel, so account for it
          networkRef.current.setSize(`${window.innerWidth}px`, `${window.innerHeight - 60}px`);
          networkRef.current.redraw();
          networkRef.current.fit(); // Adjust view to show all nodes
        }
      }, 100);
    } else {
      // Exit fullscreen
      
      // Add a delay to let the DOM update
      setTimeout(() => {
        if (networkRef.current) {
          networkRef.current.setSize('100%', '100%');
          networkRef.current.redraw();
          networkRef.current.fit(); // Adjust view to show all nodes
        }
      }, 100);
    }
  }, [isFullscreen]);

  const handlePhysicsSettingsChange = (newSettings: any) => {
    setPhysicsSettings(newSettings);

    if (networkRef.current) {
      networkRef.current.setOptions({
        physics: {
          ...graphVizBehaviourOptions.physics,
          forceAtlas2Based: newSettings
        }
      });

      // If the graph is already stabilized, re-enable physics temporarily to apply changes
      if (stabilizationComplete) {
        networkRef.current.setOptions({ physics: { enabled: true } });
        setTimeout(() => {
          // Let the physics run for a brief moment before re-stabilizing
          networkRef.current?.stabilize(50);
        }, 50);
      }
    }
  };

  // Add window resize handler for fullscreen mode
  useEffect(() => {
    const handleWindowResize = () => {
      if (isFullscreen && networkRef.current) {
        networkRef.current.setSize(`${window.innerWidth}px`, `${window.innerHeight - 60}px`);
        networkRef.current.redraw();
      }
    };

    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [isFullscreen]);

  // Track previous orgNumber to detect graph switches
  const prevOrgNumberRef = useRef<string>(orgNumber);

  useEffect(() => {
    const isGraphSwitch = prevOrgNumberRef.current !== orgNumber;
    prevOrgNumberRef.current = orgNumber;

    // Store current positions if the network exists and has been stabilized
    let storedPositions: { [id: string]: { x: number, y: number } } | null = null;
    if (networkRef.current && stabilizationComplete && !isGraphSwitch) {
      storedPositions = getCurrentNodePositions();
    }

    // Check if nodes have saved positions (indicating case load)
    const loadedFromCase = hasNodesWithPositions(nodes);

    // Merge props with existing dataset to preserve properties learned during expansion
    // This fixes the issue where expanding a node updates the dataset, but this Effect reverts it 
    // because 'nodes' prop is stale.
    let nodesToProcess = nodes;
    if (!isGraphSwitch && graphNodes.length > 0) {
      const currentDataSet = graphNodes.get({ returnType: 'Object' }) as Record<string, any>;
      nodesToProcess = nodes.map(n => {
        const existing = currentDataSet[String(n.id)];
        if (existing && existing.properties) {
          // Preserve properties learned during expansion while allowing a new meaningful
          // value to replace an older empty value (for example a car description).
          return {
             ...n,
             x: existing.x ?? (n as any).x,
             y: existing.y ?? (n as any).y,
             properties: mergeGraphNodeProperties(n.properties, existing.properties)
          };
        }
        return n;
      });
    }

    let nodesList = getAllNodesWithLabels(
      nodesToProcess,
      orgNumber,
      graphMetaData,
      new Set(),
      new Set(),
      false,
      new Set(),
      nodeLabelSettings,
      isDarkMode,
      shouldShowCompanyComments,
    );
    
    // Apply stored positions to nodes to prevent movement during updates
    if (storedPositions) {
      nodesList = nodesList.map(node => {
        if (storedPositions && storedPositions[node.id]) {
          return {
            ...node,
            x: storedPositions[node.id].x,
            y: storedPositions[node.id].y,
          };
        }
        return node;
      });
    }

    const edgesList = getAllEdgesWithLabels(filteredRelationships, graphVizBehaviourOptions, getAccessToken(), nodes, new Set(), false, edgeLabelSettings, currentLanguage);

    // If we are just updating the graph (e.g. time slider) and not switching graphs,
    // update the existing datasets instead of replacing them to avoid view reset.
    if (!isGraphSwitch && stabilizationComplete && graphNodes.length > 0 && networkRef.current) {
      // Ensure physics is disabled
      networkRef.current.setOptions({ physics: { enabled: false } });
      
      // Update nodes: only update existing ones, don't remove/add unless necessary
      // Since 'nodes' prop hasn't changed, we just update properties and positions
      graphNodes.update(nodesList);
      
      // Update edges: Sync the dataset (remove missing, add/update new)
      const currentEdgeIds = new Set(graphEdges.getIds());
      const newEdgeIds = new Set(edgesList.map(e => e.id));
      
      const edgesToRemove = Array.from(currentEdgeIds).filter(id => !newEdgeIds.has(id as string));
      
      if (edgesToRemove.length > 0) {
          graphEdges.remove(edgesToRemove);
      }
      graphEdges.update(edgesList);
      
      setGraphDataVersion(prev => prev + 1);
      
      // No need to call setData or reset stabilization
    } else {
      // Full reset (new graph or initial load)
      const allNodesWithLabels = new DataSet(nodesList);
      const allEdgesWithLabels = new DataSet(edgesList);

      setGraphNodes(allNodesWithLabels);
      setGraphEdges(allEdgesWithLabels);
      setGraphDataVersion(prev => prev + 1);

      networkRef.current?.setData({ nodes: allNodesWithLabels, edges: allEdgesWithLabels });

      if (loadedFromCase) {
        // Case loading: nodes have saved positions. Use these positions instead of restabilizing.
        setStabilizationComplete(true);
        setStabilizationProgress(100);
      } else {
        // Fresh data: reset stabilization state and let physics run
        console.log('🔄 Loading fresh data - enabling physics for stabilization');
        setStabilizationComplete(false);
        setStabilizationProgress(0);
      }
      
      // Only reset hiddenStateStack when switching graphs
      if (hiddenStateStackProp) {
        hiddenStateStack.current = hiddenStateStackProp;
      } else {
        hiddenStateStack.current = new HiddenStateStack();
      }
    }
    
    // Get unique graph types from nodes and relationships
    const uniqueGraphTypes = extractUniqueGraphTypes(filteredRelationships);
    setAvailableGraphTypes(uniqueGraphTypes);

    setUserHiddenNodes(hiddenStateStack.current.getState(getGraphTypesCompositeKey(selectedGraphTypes)));
  }, [nodes, filteredRelationships, orgNumber, getCurrentNodePositions, currentLanguage]); // Removed highlightedNodes from dependency array

  // Effect to update highlighted nodes when search terms change
  useEffect(() => {
    if (!graphNodes) return;
    
    // Get all current nodes from the graph (including expanded nodes)
    const allCurrentNodes = graphNodes.get();
    const newHighlightedNodes = getHighlightedNodes(allCurrentNodes, searchTerms);
    setHighlightedNodes(newHighlightedNodes);
  }, [nodes, searchTerms, graphNodes, graphDataVersion]);

  // Effect to update labels when label settings change
  useEffect(() => {
    if (!graphNodes || graphNodes.length === 0 || !graphEdges || graphEdges.length === 0 || !networkRef.current || !stabilizationComplete) return;

    // Store current positions before making any changes
    const currentPositions = getCurrentNodePositions();
    
    // Update all nodes with new label settings
    const currentNodes = graphNodes.get();
    const nodesToUpdate = currentNodes.map(node => {
      const updatedNode = { ...node };
      ApplyNodeStyling(updatedNode, orgNumber, graphMetaData, highlightedNodes, newlyAddedNodes, showNewAdditions, expandedNodes, nodeLabelSettings, isDarkMode, shouldShowCompanyComments);
      
      // Keep positions and ensure nodes remain draggable
      const currentPos = currentPositions[node.id];
      return {
        ...updatedNode,
        x: currentPos?.x ?? (node as any).x,
        y: currentPos?.y ?? (node as any).y,
        fixed: undefined
      };
    });

    // Update all edges with new label settings
    const currentEdges = graphEdges.get();
    const relationshipFormatEdges = currentEdges.map(edge => ({
      id: edge.id,
      from: edge.from,
      to: edge.to,
      properties: edge.properties || {},
      metadata: edge.metadata || {}
    }));
    
    const edgesToUpdate = getAllEdgesWithLabels(
      relationshipFormatEdges,
      graphVizBehaviourOptions,
      getAccessToken(),
      currentNodes,
      newlyAddedEdges,
      showNewAdditions,
      edgeLabelSettings
    );

    // Ensure physics stays disabled during the update
    networkRef.current.setOptions({ physics: { enabled: false } });
    
    // Use update() instead of clear()+add() to preserve viewport state
    graphNodes.update(nodesToUpdate);
    graphEdges.update(edgesToUpdate);
    
    console.log('🏷️ Updated labels based on new settings');
  }, [nodeLabelSettings, edgeLabelSettings, orgNumber, graphMetaData, stabilizationComplete, isDarkMode, getCurrentNodePositions]);

  // Effect to re-style edges and node fonts when dark mode changes
  useEffect(() => {
    if (!graphNodes || graphNodes.length === 0 || !graphEdges || graphEdges.length === 0 || !networkRef.current || !stabilizationComplete) return;

    // Store current positions before making any changes
    const currentPositions = getCurrentNodePositions();

    // Update network-level options (edge defaults, font colors)
    const { edgeColors: _ec, ...edgeOptionsForNetwork } = graphVizBehaviourOptions.edges;
    networkRef.current.setOptions({
      nodes: graphVizBehaviourOptions.nodes,
      edges: edgeOptionsForNetwork,
      physics: { enabled: false }
    });

    // Re-style all nodes with updated font colors
    const currentNodes = graphNodes.get();
    const nodesToUpdate = currentNodes.map(node => {
      const updatedNode = { ...node };
      ApplyNodeStyling(updatedNode, orgNumber, graphMetaData, highlightedNodes, newlyAddedNodes, showNewAdditions, expandedNodes, nodeLabelSettings, isDarkMode, shouldShowCompanyComments);
      const currentPos = currentPositions[node.id];
      return {
        ...updatedNode,
        x: currentPos?.x ?? (node as any).x,
        y: currentPos?.y ?? (node as any).y,
        fixed: undefined
      };
    });

    // Re-style all edges with updated colors for the new mode
    const currentEdges = graphEdges.get();
    const relationshipFormatEdges = currentEdges.map(edge => ({
      id: edge.id,
      from: edge.from,
      to: edge.to,
      properties: edge.properties || {},
      metadata: edge.metadata || {}
    }));

    const edgesToUpdate = getAllEdgesWithLabels(
      relationshipFormatEdges,
      graphVizBehaviourOptions,
      getAccessToken(),
      currentNodes,
      newlyAddedEdges,
      showNewAdditions,
      edgeLabelSettings
    );

    graphNodes.update(nodesToUpdate);
    graphEdges.update(edgesToUpdate);

  }, [isDarkMode, stabilizationComplete, getCurrentNodePositions]);

  // Effect to shift viewport when stabilization is complete - MOVED TO stabilizationIterationsDone
  // to prevent visual jumping
  // useEffect(() => {
  //   if (stabilizationComplete && networkRef.current && containerRef.current) {
  //     const width = containerRef.current.clientWidth;
  //     networkRef.current.moveTo({
  //       offset: { x: width * 0.20, y: 0 }
  //     });
  //   }
  // }, [stabilizationComplete]);

  // Effect to trigger the graph reveal animation after stabilization completes
  // If the slide animation already revealed the graph, this is a no-op.
  useEffect(() => {
    if (stabilizationComplete && !graphRevealed) {
      // Fallback: if no slide animation ran (e.g. case load with saved positions),
      // reveal normally with a short delay
      const timer = setTimeout(() => {
        setGraphRevealed(true);
        hasRevealedOnceRef.current = true;
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [stabilizationComplete, graphRevealed]);

  // Effect to update styling when new additions highlighting changes
  useEffect(() => {
    if (!graphNodes || graphNodes.length === 0 || !networkRef.current || !stabilizationComplete) return;

    // Store current positions before making any changes
    const currentPositions = getCurrentNodePositions();
    
    // Update all nodes to reflect new additions highlighting state
    const currentNodes = graphNodes.get();
    const nodesToUpdate = currentNodes.map(node => {
      const updatedNode = { ...node };
      ApplyNodeStyling(updatedNode, orgNumber, graphMetaData, highlightedNodes, newlyAddedNodes, showNewAdditions, expandedNodes, nodeLabelSettings, isDarkMode, shouldShowCompanyComments);
      
      // Ensure nodes remain draggable by not setting fixed properties
      // Keep positions but remove any fixed constraints
      const currentPos = currentPositions[node.id];
      return {
        ...updatedNode,
        x: currentPos?.x ?? (node as any).x,
        y: currentPos?.y ?? (node as any).y,
        fixed: undefined // Always ensure nodes are not fixed
      };
    });

    // Update all edges to reflect new additions highlighting state
    // Convert current edges back to relationship format for getAllEdgesWithLabels
    const currentEdges = graphEdges.get();
    const relationshipFormatEdges = currentEdges.map(edge => ({
      id: edge.id,
      from: edge.from,
      to: edge.to,
      properties: edge.properties || {},
      metadata: edge.metadata || {}
    }));
    
    const edgesToUpdate = getAllEdgesWithLabels(
      relationshipFormatEdges,
      graphVizBehaviourOptions,
      getAccessToken(),
      currentNodes,
      newlyAddedEdges,
      showNewAdditions,
      edgeLabelSettings
    );

    // Ensure physics stays disabled during the update
    networkRef.current.setOptions({ physics: { enabled: false } });
    
    // Use update() instead of clear()+add() to preserve viewport state
    graphNodes.update(nodesToUpdate);
    graphEdges.update(edgesToUpdate);
    
    console.log('🎨 Updated node styling - all nodes should remain draggable');
  }, [showNewAdditions, newlyAddedNodes, newlyAddedEdges, orgNumber, graphMetaData, stabilizationComplete, highlightedNodes, expandedNodes, nodeLabelSettings, edgeLabelSettings, isDarkMode, getCurrentNodePositions]);

  // Separate effect to update only node styling when highlighting changes
  // This doesn't affect visibility or hidden states - only visual styling
  useEffect(() => {
    if (!graphNodes || graphNodes.length === 0 || !networkRef.current || !stabilizationComplete) return;

    // Store ALL current positions before making any changes
    const currentPositions = getCurrentNodePositions();
    
    // Update node styling directly through vis-network without changing the dataset
    // This prevents any layout recalculation
    const currentNodes = graphNodes.get();
    const nodesToUpdate: any[] = [];
    
    currentNodes.forEach(node => {
      const shouldBeHighlighted = highlightedNodes.has(node.id);
      const isCurrentlyHighlighted = (node as any).isSearchHighlighted || false;
      
      if (shouldBeHighlighted !== isCurrentlyHighlighted) {
        // Create a copy of the node with updated styling
        const updatedNode = { ...node } as any;
        ApplyNodeStyling(updatedNode, orgNumber, graphMetaData, highlightedNodes, newlyAddedNodes, showNewAdditions, expandedNodes, nodeLabelSettings, isDarkMode, shouldShowCompanyComments);
        
        // Ensure node remains draggable
        const currentPos = currentPositions[node.id];
        updatedNode.x = currentPos?.x ?? (node as any).x;
        updatedNode.y = currentPos?.y ?? (node as any).y;
        updatedNode.fixed = undefined; // Always ensure nodes are not fixed
        
        nodesToUpdate.push(updatedNode);
      }
    });

    if (nodesToUpdate.length > 0) {
      // Ensure physics stays disabled during the update
      networkRef.current.setOptions({ physics: { enabled: false } });
      
      // Update only the nodes that changed
      graphNodes.update(nodesToUpdate);
      
      console.log('🔍 Updated search highlighting - nodes remain draggable');
    }
  }, [highlightedNodes, orgNumber, graphMetaData, stabilizationComplete, nodeLabelSettings, isDarkMode, getCurrentNodePositions]);

  useEffect(() => {
    var edgesToShow: Edge[] = graphEdges.get();
    const allNodes = graphNodes.get();
    
    console.log('📊 Filtering graph - showBadPathsOnly:', showBadPathsOnly);
    console.log('📊 Total nodes:', allNodes.length);
    console.log('📊 Total edges:', edgesToShow.length);

    // Log nodes with isOnBadPath metadata
    const nodesWithBadPath = allNodes.filter(node => (node as any).metadata?.isOnBadPath);
    console.log('📊 Nodes with isOnBadPath=true:', nodesWithBadPath.length, nodesWithBadPath.map(n => ({
      id: n.id,
      name: (n as any).name,
      isOnBadPath: (n as any).metadata?.isOnBadPath
    })));

    if (showBadPathsOnly) {
        const originalEdgeCount = edgesToShow.length;
        // Filter edges to include those that are on bad paths OR connect to nodes with isOnBadPath: true
        edgesToShow = edgesToShow.filter((edge) => {
          // Include edge if it has isOnBadPath: true
          if (edge.metadata.isOnBadPath) return true;
          
          // Also include edge if either connected node has isOnBadPath: true
          const fromNode = allNodes.find(n => n.id === edge.from);
          const toNode = allNodes.find(n => n.id === edge.to);
          
          return (fromNode as any)?.metadata?.isOnBadPath || (toNode as any)?.metadata?.isOnBadPath;
        });
        console.log('📊 After bad path edge filter (includes edges to bad nodes):', edgesToShow.length, 'edges (was', originalEdgeCount + ')');
    }

    if (!selectedGraphTypes.includes('all')) {
      // We want to find edges which are reachable from the source node via relationships selected
      // by the current filters. Edges can be present on multiple paths. The cumulative relationship
      // types for each path are stored in a list.
      const beforeGraphTypeFilter = edgesToShow.length;
      edgesToShow = getEdgesForGraphType(selectedGraphTypes, edgesToShow);
      console.log('📊 After graph type filter:', edgesToShow.length, 'edges (was', beforeGraphTypeFilter + ')');
    }

    const beforeHiddenFilter = edgesToShow.length;
    edgesToShow = edgesToShow.filter((e) => !(userHiddenNodes.has(e.from) || userHiddenNodes.has(e.to)));
    console.log('📊 After hidden nodes filter:', edgesToShow.length, 'edges (was', beforeHiddenFilter + ')');

    // Determine which nodes to show based on filtering mode
    let nodeIds: Set<string>;
    
    if (showBadPathsOnly) {
      // When showing bad paths only, start fresh and only include:
      // 1. Nodes with isOnBadPath: true (unless hidden by user)
      // 2. The source node (always included)
      nodeIds = new Set();
      
      allNodes.forEach(node => {
        if ((node as any).metadata?.isOnBadPath && !userHiddenNodes.has(node.id)) {
          nodeIds.add(node.id);
        }
      });
      
      // Always include source node
      nodeIds.add(graphMetaData.sourceNodeId);
      
      console.log('📊 Bad paths only - nodes with isOnBadPath=true + source:', nodeIds.size, 'nodes');
    } else {
      // Normal mode: include nodes connected by the filtered edges
      nodeIds = new Set(edgesToShow.map((e) => e.from).concat(edgesToShow.map((e) => e.to)));
      
      // Ensure that the source node is always included
      nodeIds.add(graphMetaData.sourceNodeId);
    }
    
    var nodesToShow = allNodes.filter((n) => nodeIds.has(n.id));
    if (props.geographicCompanyIds) {
      const eligibleCompanies = new Set([...props.geographicCompanyIds].filter(id => nodeIds.has(id)
        || (!showBadPathsOnly && selectedGraphTypes.includes('all'))));
      const geographicNodeIds = getGeographicNodeIds(allNodes, edgesToShow, eligibleCompanies);
      // Keep selected companies even when their connecting company is outside the area.
      nodesToShow = allNodes.filter(node => geographicNodeIds.has(node.id) && !userHiddenNodes.has(node.id));
    }
    
    console.log('📊 Final nodes to show:', nodesToShow.length, nodesToShow.map(n => ({
      id: n.id,
      name: (n as any).name,
      isOnBadPath: (n as any).metadata?.isOnBadPath
    })));

    const nodeIdsToShow = new Set(nodesToShow.map((n) => n.id));
    
    // Filter edges to only include those connecting the nodes we want to show
    const finalEdgesToShow = edgesToShow.filter((e) => 
      nodeIdsToShow.has(e.from) && nodeIdsToShow.has(e.to)
    );
    
    const edgeIdsToShow = new Set(finalEdgesToShow.map((e) => e.id));

    const path = pathTargetId ? findConnectionPath(nodesToShow, finalEdgesToShow, String(graphMetaData.sourceNodeId), pathTargetId) : null;
    setConnectionPath(path);
    const pathNodeIds = new Set(path?.nodeIds);
    const pathEdgeIds = new Set(path?.edges.map(edge => String(edge.id)));
    
    console.log('📊 Final edges to show (connecting filtered nodes):', finalEdgesToShow.length);

    // Save the graph positions
    networkRef.current?.storePositions();

    // Create updated nodes and edges respecting both filters and manual hiding
    // Apply current highlighting state to visible nodes only  
    const nodeUpdates = graphNodes.get().map((n) => {
      const updatedNode = { ...n, hidden: !nodeIdsToShow.has(n.id), fixed: false, opacity: path && !pathNodeIds.has(String(n.id)) ? 0.2 : 1 };
      // Only apply styling to visible nodes to avoid unnecessary work
      // Get the current highlighting state at the time of filtering
      if (!updatedNode.hidden) {
        ApplyNodeStyling(updatedNode, orgNumber, graphMetaData, highlightedNodes, newlyAddedNodes, showNewAdditions, expandedNodes, nodeLabelSettings, isDarkMode, shouldShowCompanyComments);
      }
      return updatedNode;
    });

    // Re-style edges so that highlight colors (e.g. green for newly added) are
    // properly applied or removed when showNewAdditions / newlyAddedEdges change.
    const currentEdges = graphEdges.get();
    const relationshipFormatEdges = currentEdges.map(edge => ({
      id: edge.id,
      from: edge.from,
      to: edge.to,
      properties: edge.properties || {},
      metadata: edge.metadata || {}
    }));
    const restyledEdges = getAllEdgesWithLabels(
      relationshipFormatEdges,
      graphVizBehaviourOptions,
      getAccessToken(),
      nodeUpdates,
      newlyAddedEdges,
      showNewAdditions,
      edgeLabelSettings
    );
    // Build a map for quick lookup of restyled edge data
    const restyledEdgeMap = new Map(restyledEdges.map((e: any) => [String(e.id), e]));

    const edgeUpdates = currentEdges.map((e) => {
      const restyled = restyledEdgeMap.get(String(e.id));
      return {
        ...(restyled || e),
        color: path ? {
          color: pathEdgeIds.has(String(e.id)) ? '#0891b2' : (restyled as any)?.color?.color || '#94a3b8',
          highlight: '#0891b2', hover: '#0891b2',
          opacity: pathEdgeIds.has(String(e.id)) ? 1 : 0.12,
        } : { ...((restyled as any)?.color || {}), opacity: 1 },
        width: pathEdgeIds.has(String(e.id)) ? 3 : (restyled as any)?.width ?? (graphVizBehaviourOptions.edges as any).width ?? 1,
        hidden: !edgeIdsToShow.has(e.id)
      };
    });

    graphNodes.update(nodeUpdates);
    graphEdges.update(edgeUpdates);
  }, [props.geographicCompanyIds, showBadPathsOnly, selectedGraphTypes, userHiddenNodes, orgNumber, graphMetaData, stabilizationComplete, highlightedNodes, expandedNodes, nodeLabelSettings, edgeLabelSettings, graphDataVersion, showNewAdditions, newlyAddedNodes, newlyAddedEdges, pathTargetId, isDarkMode, shouldShowCompanyComments]);

  // Effect to drive the pulsing animation for debt badges
  useEffect(() => {
    let animationFrameId: number;
    let isAnimating = false;

    const animate = () => {
      if (networkRef.current) {
        networkRef.current.redraw();
      }
      if (isAnimating) {
        animationFrameId = requestAnimationFrame(animate);
      }
    };

    const checkAndStartAnimation = () => {
      if (!graphNodes) return;
      
      // Check if we have any visible nodes with debt connections
      const allNodes = graphNodes.get();
      const hasVisibleDebtNodes = allNodes.some((node: any) => {
        // Skip hidden nodes
        if (node.hidden === true) return false;
        // Skip expanded nodes
        if (expandedNodes.has(node.id)) return false;
        // Check for debt
        const aggData = node.properties;
        return aggData && aggData.next_layer_active_debt_count !== undefined && aggData.next_layer_active_debt_count > 0;
      });
      
      if (hasVisibleDebtNodes) {
        if (!isAnimating) {
          isAnimating = true;
          animate();
        }
      } else {
        isAnimating = false;
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
        }
      }
    };

    checkAndStartAnimation();

    return () => {
      isAnimating = false;
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [graphNodes, expandedNodes, userHiddenNodes, showBadPathsOnly, selectedGraphTypes, graphDataVersion]);

  const hideBranch = (nodeId: string) => {
    if (isFetchingData || isExpansionLoading || !stabilizationComplete) return;
    const visibleNodes = graphNodes.get().filter(node => !(node as any).hidden);
    const visibleEdges = graphEdges.get().filter(edge => !(edge as any).hidden);
    const toHide = getBranchToHide(visibleNodes, visibleEdges, String(graphMetaData.sourceNodeId), nodeId);
    if (!toHide.size) return;
    const key = getGraphTypesCompositeKey(selectedGraphTypes);
    const before = hiddenStateStack.current.toJSON();
    hiddenStateStack.current.insertState(key, toHide);
    setHiddenBranchNotice({ count: toHide.size, key, before, after: JSON.stringify(hiddenStateStack.current.toJSON()) });
    setUserHiddenNodes(hiddenStateStack.current.getState(key));
    setSelectedNodeId(null);
    setPathTargetId(null);
    networkRef.current?.unselectAll();
  };

  const undoHiddenBranch = () => {
    if (!hiddenBranchNotice || JSON.stringify(hiddenStateStack.current.toJSON()) !== hiddenBranchNotice.after) return;
    hiddenStateStack.current = HiddenStateStack.fromJSON(hiddenBranchNotice.before);
    setUserHiddenNodes(hiddenStateStack.current.getState(hiddenBranchNotice.key));
    setHiddenBranchNotice(null);
  };

  const events = useGraphEvents({
    networkRef,
    graphNodes,
    graphEdges,
    expandedNodes,
    isExpanding,
    dragSelectionActiveRef,
    selectionRectRef,
    isSelectingRef,
    setStabilizationProgress,
    setStabilizationComplete,
    containerRef,
    setContextMenuParams,
    setShowContextMenu,
    graphMetaData,
    selectedGraphTypes,
    userHiddenNodes,
    hiddenStateStack,
    setUserHiddenNodes,
    updateNodePosition,
    showCompanyComments: shouldShowCompanyComments,
    showIvoLicenses: shouldShowIvoLicenseOverlay,
    onSelectedNodeChange: setSelectedNodeId,
    onHideBranch: hideBranch,
    onInitialStabilizationDone: handleInitialStabilizationDone
  });

  useEffect(() => {
    if (!graphNodes || graphNodes.length === 0) {
      return;
    }

    const currentPositions = getCurrentNodePositions();
    const currentNodes = graphNodes.get();
    const nodesToUpdate = currentNodes.map((node) => {
      const updatedNode = { ...node };
      ApplyNodeStyling(
        updatedNode,
        orgNumber,
        graphMetaData,
        highlightedNodes,
        newlyAddedNodes,
        showNewAdditions,
        expandedNodes,
        nodeLabelSettings,
        isDarkMode,
        shouldShowCompanyComments,
      );

      const currentPos = currentPositions[node.id];
      return {
        ...updatedNode,
        x: currentPos?.x ?? (node as any).x,
        y: currentPos?.y ?? (node as any).y,
      };
    });

    graphNodes.update(nodesToUpdate);
    networkRef.current?.redraw();
  }, [
    expandedNodes,
    getCurrentNodePositions,
    graphMetaData,
    graphNodes,
    highlightedNodes,
    isDarkMode,
    newlyAddedNodes,
    nodeLabelSettings,
    orgNumber,
    shouldShowCompanyComments,
    showNewAdditions,
  ]);

  const onShowBadLinksUpdated = (showBadLinks: boolean) => {
    console.log('🎯 onShowBadLinksUpdated called with:', showBadLinks);
    setShowBadPathsOnly(showBadLinks);
  };

  const onLinkingOptionsUpdated = (linkingOptions: string[]) => {
    setHiddenBranchNotice(null);
    // Create a composite key for tracking in hiddenStateStack
    const graphTypesKey = getGraphTypesCompositeKey(linkingOptions);
    
    setSelectedGraphTypes(linkingOptions);
    setUserHiddenNodes(hiddenStateStack.current.getState(graphTypesKey));
  };

  const goBack = () => {
    setHiddenBranchNotice(null);
    // Create a composite key for tracking in hiddenStateStack
    const graphTypesKey = getGraphTypesCompositeKey(selectedGraphTypes);
    
    hiddenStateStack.current.goBack(graphTypesKey);
    setUserHiddenNodes(hiddenStateStack.current.getState(graphTypesKey));
  };

  const goForward = () => {
    setHiddenBranchNotice(null);
    // Create a composite key for tracking in hiddenStateStack
    const graphTypesKey = getGraphTypesCompositeKey(selectedGraphTypes);
    
    hiddenStateStack.current.goForward(graphTypesKey);
    setUserHiddenNodes(hiddenStateStack.current.getState(graphTypesKey));
  };

  // Search functionality handlers
  const handleAddSearchTerm = (term: string) => {
    const newSearchTerm: SearchTerm = {
      id: `${Date.now()}-${Math.random()}`,
      value: term,
      addedAt: new Date()
    };
    const updatedSearchTerms = [...searchTerms, newSearchTerm];
    setSearchTerms(updatedSearchTerms);
    
    // Immediately update highlighting for all nodes including newly added ones
    if (graphNodes) {
      const allCurrentNodes = graphNodes.get();
      const newHighlightedNodes = getHighlightedNodes(allCurrentNodes, updatedSearchTerms);
      setHighlightedNodes(newHighlightedNodes);
    }
  };

  const handleRemoveSearchTerm = (id: string) => {
    const newSearchTerms = searchTerms.filter(term => term.id !== id);
    setSearchTerms(newSearchTerms);
    
    // Immediately update highlighting for all nodes
    if (graphNodes) {
      const allCurrentNodes = graphNodes.get();
      const newHighlightedNodes = getHighlightedNodes(allCurrentNodes, newSearchTerms);
      setHighlightedNodes(newHighlightedNodes);
    }
    
    // Clear saved viewport when no search terms remain
    if (newSearchTerms.length === 0) {
      viewportStateManager.clearSavedViewport();
    }
  };

  const handleClearAllSearchTerms = () => {
    setSearchTerms([]);
    
    // Clear all search highlighting
    setHighlightedNodes(new Set());
    
    // Clear saved viewport when clearing all search terms
    viewportStateManager.clearSavedViewport();
  };

  const handleToggleSearchPanelMinimize = () => {
    setIsSearchPanelMinimized(prev => !prev);
  };

  const handleToggleSidePanel = () => {
    setIsSidePanelOpen(prev => !prev);
  };

  const handleSelectEntity = (entry: EntityListEntry) => {
    if (networkRef.current) {
      networkRef.current.focus(entry.id, {
        scale: 1.0,
        animation: {
          duration: 1000,
          easingFunction: 'easeInOutQuad'
        }
      });
      networkRef.current.selectNodes([entry.id]);
      setSelectedNodeId(entry.id);
    }
  };

  // New additions highlighting handlers
  const handleClearNewAdditions = () => {
    setNewlyAddedNodes(new Set());
    setNewlyAddedEdges(new Set());
    setShowNewAdditions(false);
    cancelSlideAnimation();
  };

  const clearSelectedNode = useCallback(() => {
    setSelectedNodeId(null);
    setPathTargetId(null);
    networkRef.current?.unselectAll();
  }, []);

  // Full restabilization handler
  const handleFullRestabilization = () => {
    if (!networkRef.current || !graphNodes) {
      return;
    }
    
    // Reset stabilization state
    setStabilizationComplete(false);
    setStabilizationProgress(0);
    
    // Store current hidden states so we can restore them later
    const allNodes = graphNodes.get();
    const originalHiddenStates = new Map(allNodes.map(node => [node.id, (node as any).hidden || false]));
    
    // Temporarily show ALL nodes (including hidden ones) and unlock their positions
    const unlockedNodes = allNodes.map(node => ({
      ...node,
      hidden: false, // Temporarily show all nodes
      fixed: undefined,
      x: undefined,
      y: undefined
    }));
    
    graphNodes.update(unlockedNodes);
    
    // Re-enable physics with original settings and start stabilization
    networkRef.current.setOptions({
      physics: {
        ...graphVizBehaviourOptions.physics,
        forceAtlas2Based: physicsSettings,
        enabled: true
      }
    });
    
    // Add a one-time listener to restore hidden states after stabilization
    const restoreHiddenStatesAfterStabilization = () => {
      // Wait a bit for stabilization to complete, then restore hidden states
      setTimeout(() => {
        if (graphNodes) {
          const stabilizedNodes = graphNodes.get();
          const restoredNodes = stabilizedNodes.map(node => ({
            ...node,
            hidden: originalHiddenStates.get(node.id) || false
          } as any));
          graphNodes.update(restoredNodes);
        }
      }, 100);
    };
    
    // Listen for stabilization completion to restore hidden states
    networkRef.current.once('stabilizationIterationsDone', restoreHiddenStatesAfterStabilization);
    
    // Trigger manual stabilization
    networkRef.current.stabilize();
  };







  // Calculate summary statistics based on current graph nodes (including expanded)
  const summaryStats = useMemo(() => {
    const allNodes = graphNodes.get();
    const visibleCompanies = allNodes.filter(n => n.type === 'Company' && !userHiddenNodes.has(n.id)
      && (!props.geographicCompanyIds || props.geographicCompanyIds.has(n.id)));
    
    return {
      total: visibleCompanies.length,
      watchlist: visibleCompanies.filter(n => (n as any).isOnWatchList).length,
      debt: visibleCompanies.filter(n => (n.properties?.total_debt && n.properties.total_debt > 0)).length,
      ivoLicenses: visibleCompanies.filter(n => n.properties?.has_ivo_license === true).length,
      tenantCaseOrganizations: countOrganizationsWithTenantCases(visibleCompanies),
      bankruptcy: visibleCompanies.filter(n => 
        (n.properties?.konkurs_inledd && n.properties?.konkurs_inledd !== "") && 
        (!n.properties?.konkurs_avslutad || n.properties?.konkurs_avslutad === "")
      ).length
    };
  }, [graphNodes, userHiddenNodes, graphDataVersion, orgNumber, props.geographicCompanyIds]);

  const selectedNodeDetails = useMemo(() => {
    if (!selectedNodeId) {
      return null;
    }

    const selectedNode = graphNodes.get(selectedNodeId);
    if (!selectedNode) {
      return null;
    }

    const nodeTitle = (selectedNode as any).title;
    const detailsHtml =
      nodeTitle instanceof HTMLElement
        ? nodeTitle.innerHTML
        : typeof nodeTitle === 'string'
          ? nodeTitle
          : '';

    const displayName =
      selectedNode.properties?.organization_name ||
      selectedNode.properties?.funktionar_namn ||
      selectedNode.properties?.street ||
      (selectedNode as any).name ||
      selectedNode.properties?.organization_number ||
      selectedNode.id;

    return {
      id: selectedNode.id,
      node: selectedNode,
      type: selectedNode.type,
      displayName: String(displayName),
      html: detailsHtml.trim(),
    };
  }, [
    expandedNodes,
    graphDataVersion,
    graphNodes,
    highlightedNodes,
    newlyAddedNodes,
    nodeLabelSettings,
    selectedNodeId,
    shouldShowCompanyComments,
    showNewAdditions,
  ]);

  const selectedNodeDetailsWithContent = selectedNodeDetails;

  // Process entities for side panel
  const { companies: companiesList, addresses: addressesList, cars: carsList } = React.useMemo(() => {
    const geographicNodeIds = props.geographicCompanyIds ? getGeographicNodeIds(nodes, relationships, props.geographicCompanyIds) : null;
    const { companies, addresses, cars } = processGraphEntities(
      geographicNodeIds ? nodes.filter(node => geographicNodeIds.has(node.id)) : nodes,
      geographicNodeIds ? relationships.filter(edge => geographicNodeIds.has(edge.from) && geographicNodeIds.has(edge.to)) : relationships,
    );
    
    return {
      companies: companies.map(c => ({ ...c, isHidden: userHiddenNodes.has(c.id) })),
      addresses: addresses.map(a => ({ ...a, isHidden: userHiddenNodes.has(a.id) })),
      cars: cars.map(car => ({ ...car, isHidden: userHiddenNodes.has(car.id) })),
    };
  }, [nodes, relationships, userHiddenNodes, props.geographicCompanyIds]);

  const visGraphOptions = useMemo(() => ({
    ...graphVizBehaviourOptions,
    physics: {
      ...graphVizBehaviourOptions.physics,
      forceAtlas2Based: physicsSettings,
      enabled: !stabilizationComplete
    }
  }), [graphVizBehaviourOptions, physicsSettings, stabilizationComplete]);

  const emptyGraphData = useMemo(() => ({ nodes: [], edges: [] }), []);

  return (
    <>
      <Box
        ref={containerRef}
        className={`org-graph-container ${isFullscreen ? 'fullscreen-mode' : ''} ${isDarkMode ? 'dark-mode' : ''}`}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          boxSizing: 'border-box',
          backgroundColor: `${(isDarkMode || !orgNumber) ? '#000000' : theme.palette.background.default} !important`,
          color: `${theme.palette.text.primary} !important`,
          ...(isFullscreen && {
            backgroundColor: `${(isDarkMode || !orgNumber) ? '#000000' : theme.palette.background.default} !important`,
            zIndex: 10000,
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw !important',
            height: '100vh !important',
            '& .vis-network': {
              pointerEvents: 'auto',  // Ensure pointer events work
              touchAction: 'auto'     // Ensure touch events work
            }
          })
        }}
      >
        <OrgGraphTopPanel
          disabled={!stabilizationComplete}
          onShowBadLinksUpdated={onShowBadLinksUpdated}
          activeRelationshipsOnly={activeRelationshipsOnly}
          onActiveRelationshipsOnlyChange={setActiveRelationshipsOnly}
          onLinkingOptionsUpdated={onLinkingOptionsUpdated}
          showFilterButtonsValue={showFilterButtons}
          onBack={goBack}
          onForward={goForward}
          canGoBack={!hiddenStateStack.current.atStartState(getGraphTypesCompositeKey(selectedGraphTypes))}
          canGoForward={!hiddenStateStack.current.atEndState(getGraphTypesCompositeKey(selectedGraphTypes))}
          onToggleFullscreen={toggleFullscreen}
          isFullscreen={isFullscreen}
          availableGraphTypes={availableGraphTypes}
          selectedGraphTypes={selectedGraphTypes}
          onRestabilize={handleFullRestabilization}
          physicsSettings={physicsSettings}
          onPhysicsSettingsChange={handlePhysicsSettingsChange}
          showNewAdditions={showNewAdditions}
          onClearNewAdditions={handleClearNewAdditions}
          nodeLabelSettings={nodeLabelSettings}
          edgeLabelSettings={edgeLabelSettings}
          onNodeLabelSettingsChange={handleNodeLabelSettingsChange}
          onEdgeLabelSettingsChange={handleEdgeLabelSettingsChange}
          graphVisRef={{ current: { getGraphState, exportGraphAsPNG, exportGraphAsSVG, getMaxScale, getNodeLabelSettings: () => nodeLabelSettings, setNodeLabelSettings: handleNodeLabelSettingsChange, getEdgeLabelSettings: () => edgeLabelSettings, setEdgeLabelSettings: handleEdgeLabelSettingsChange } }}
          leftSpacerWidth={props.topPanelLeftSpacerWidth}
          legendLeftOffset={props.legendLeftOffset}
          isDarkMode={isDarkMode}
          onToggleSearch={handleToggleSearchPanelMinimize}
          isSearchOpen={!isSearchPanelMinimized}
          topBarContent={props.topBarContent}
          enableCompanyComments={enableCompanyComments}
          showCompanyComments={showCompanyComments}
          onToggleCompanyComments={enableCompanyComments ? () => setShowCompanyComments((previous) => !previous) : undefined}
          enableIvoLicenses={shouldShowIvoLicenseOverlay}
          enableLeasedCars={hasLeasedCarsNodes}
          enableTenantCases={enableTenantCases}
          isSidePanelOpen={isSidePanelOpen}
          onToggleSidePanel={handleToggleSidePanel}
          isSummaryOpen={isSummaryVisible}
          onToggleSummary={() => setIsSummaryVisible(!isSummaryVisible)}
          minDate={minDate}
          maxDate={maxDate}
          selectedDate={selectedDate}
          onDateChange={setSelectedDate}
        />
        <Box sx={{ width: '100%', position: 'relative', flexGrow: 1, minHeight: 0, overflow: 'hidden' }}>
          {isSidePanelOpen && (
            <div style={{ position: 'absolute', inset: 0, zIndex: 1200, pointerEvents: 'none', background: 'transparent' }}>
              <EntityListPanel
                companies={companiesList}
                addresses={addressesList}
                cars={carsList}
                showCarsTab={enableCarEntityList}
                enableTenantCases={enableTenantCases}
                enableIvoLicenses={enableIvoLicenseOverlay}
                relationships={relationships}
                onSelectEntity={handleSelectEntity}
                onClose={() => setIsSidePanelOpen(false)}
                mode="graph"
                sourceNodeId={props.orgNumber}
              />
            </div>
          )}
          <Box sx={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}>
            {!isSearchPanelMinimized && (
              <SearchHighlightPanel
              searchTerms={searchTerms}
              onAddSearchTerm={handleAddSearchTerm}
              onRemoveSearchTerm={handleRemoveSearchTerm}
              onClearAllSearchTerms={handleClearAllSearchTerms}
              isMinimized={false}
              onToggleMinimize={handleToggleSearchPanelMinimize}
              isFullscreen={isFullscreen}
              networkRef={networkRef}
            />
          )}
            <div className={`graph-reveal-wrapper ${graphRevealed ? 'revealed' : ''} ${hasRevealedOnceRef.current ? 'no-animation' : ''}`}>
            <VisGraph
            style={{ width: '100%', height: '100%' }}
            options={visGraphOptions}
            graph={emptyGraphData}
            events={events}
            getNetwork={(network: Network) => {
              networkRef.current = network;
            }}
          />
          </div>
          
          {/* Summary Panel */}
          {isSummaryVisible && (
          <Paper
            elevation={3}
            sx={{
              position: 'absolute',
              top: 10,
              left: 10,
              zIndex: 1000,
              padding: 1.5,
              backgroundColor: isDarkMode ? 'rgba(30, 41, 59, 0.9)' : 'rgba(255, 255, 255, 0.9)',
              color: isDarkMode ? '#fff' : 'inherit',
              minWidth: 200,
              backdropFilter: 'blur(4px)',
              borderRadius: 2,
              border: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`,
              transition: 'all 0.3s ease'
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              mb: 1, 
              borderBottom: `1px solid ${isDarkMode ? '#475569' : '#e2e8f0'}`, 
              pb: 0.5 
            }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t('graph.networkSummary')}
              </Typography>
              <IconButton 
                size="small" 
                onClick={() => setIsSummaryVisible(false)}
                sx={{ padding: 0.5, color: 'inherit' }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption">{t('graph.totalCompanies')}:</Typography>
                  <Typography variant="caption" fontWeight="bold">{summaryStats.total}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption">{t('graph.onWatchlist')}:</Typography>
                  <Typography className="network-summary-number--watchlist" variant="caption" fontWeight="bold" sx={{ color: '#d97706' }}>{summaryStats.watchlist}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption">{t('graph.withKfmDebt')}:</Typography>
                  <Typography className="network-summary-number--debt" variant="caption" fontWeight="bold" sx={{ color: '#dc2626' }}>{summaryStats.debt}</Typography>
                </Box>
                {enableIvoLicenseOverlay && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption">{t('graph.withIvoLicense')}:</Typography>
                    <Typography className="network-summary-number--ivo" variant="caption" fontWeight="bold" sx={{ color: '#0f766e' }}>{summaryStats.ivoLicenses}</Typography>
                  </Box>
                )}
                {enableTenantCases && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption">{t('graph.withTenantCases', {
                      defaultValue: currentLanguage?.startsWith('sv') ? 'Företag med ärenden' : 'Companies with cases',
                    })}:</Typography>
                    <Typography className="network-summary-number--tenant-cases" variant="caption" fontWeight="bold" sx={{ color: TENANT_CASE_BADGE_COLOR }}>{summaryStats.tenantCaseOrganizations}</Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption">{t('graph.inBankruptcy')}:</Typography>
                  <Typography variant="caption" fontWeight="bold">{summaryStats.bankruptcy}</Typography>
                </Box>
            </Box>
          </Paper>
          )}

          {selectedNodeDetailsWithContent && (
            <Paper
              elevation={3}
              sx={{
                position: 'absolute',
                top: 10,
                right: 10,
                zIndex: 1000,
                width: 360,
                maxWidth: 'calc(100% - 20px)',
                maxHeight: 'min(70%, 520px)',
                overflow: 'auto',
                padding: 1.5,
                backgroundColor: isDarkMode ? 'rgba(30, 41, 59, 0.94)' : 'rgba(255, 255, 255, 0.96)',
                color: isDarkMode ? '#fff' : 'inherit',
                backdropFilter: 'blur(8px)',
                borderRadius: 2,
                border: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`,
                boxShadow: isDarkMode
                  ? '0 12px 30px rgba(15, 23, 42, 0.45)'
                  : '0 12px 30px rgba(15, 23, 42, 0.12)',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 1,
                  mb: 1,
                  pb: 1,
                  borderBottom: `1px solid ${isDarkMode ? '#475569' : '#e2e8f0'}`,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {selectedNodeDetailsWithContent.displayName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedNodeDetailsWithContent.type}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={clearSelectedNode}
                  aria-label={t('common.actions.close')}
                  sx={{ padding: 0.5, color: 'inherit', flexShrink: 0 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              <OrgGraphContextMenu
                key={selectedNodeDetailsWithContent.id}
                inlineActions
                actionsDisabled={!stabilizationComplete || isFetchingData || isExpansionLoading}
                contextMenuProps={{ x: 0, y: 0, node: selectedNodeDetailsWithContent.node }}
                showContextMenu={false}
                setShowContextMenu={setShowContextMenu}
                getAccessToken={getAccessToken}
                onExpandNode={handleNormalExpansion}
                onExpandVehicles={handleVehicleExpansion}
                setIsFetchingData={setIsFetchingData}
                expandedNodes={normallyExpandedNodes}
                expandedVehicleNodes={expandedVehicleNodes}
                sourceNodeId={graphMetaData.sourceNodeId}
                enableVehicleExpansion={enableVehicleExpansion}
                onMonitorCompany={props.onMonitorCompany}
                isConnectionPathVisible={pathTargetId === selectedNodeDetailsWithContent.id}
                onShowConnectionPath={() => setPathTargetId(previous => previous === selectedNodeDetailsWithContent.id ? null : selectedNodeDetailsWithContent.id)}
                onWatchlistChange={handleWatchlistChange}
                enableCompanyComments={enableCompanyComments}
                onSaveCompanyComment={enableCompanyComments ? handleSaveCompanyComment : undefined}
                onDeleteCompanyComment={enableCompanyComments ? handleDeleteCompanyComment : undefined}
                onHideBranch={() => hideBranch(selectedNodeDetailsWithContent.id)}
              />
              {pathTargetId && <ConnectionPathDetails path={connectionPath} nodes={graphNodes.get()} />}
              <div dangerouslySetInnerHTML={{ __html: selectedNodeDetailsWithContent.html }} />
            </Paper>
          )}
          {/* Animated loading overlay for initial stabilization */}
          {orgNumber && (
            <GraphLoadingOverlay
              progress={stabilizationProgress}
              visible={!stabilizationComplete}
              isDarkMode={isDarkMode}
            />
          )}
          
          {/* Data Fetching Status Pill */}
          <GraphStatusPill
            visible={isFetchingData}
            message={t('graph.fetchingConnections')}
            variant="fetch"
            isDarkMode={isDarkMode}
            topOffset={20}
          />

          {/* Node Expansion Status Pill */}
          <GraphStatusPill
            visible={isExpansionLoading}
            message={expansionMessage}
            variant="expand"
            isDarkMode={isDarkMode}
            topOffset={isFetchingData ? 76 : 20}
          />
          </Box>
        </Box>
      </Box>
      {contextMenuParams && (
        <OrgGraphContextMenu
          contextMenuProps={contextMenuParams}
          showContextMenu={showContextMenu}
          setShowContextMenu={setShowContextMenu}
          onCloseComplete={() => setContextMenuParams(null)}
          actionsDisabled={!stabilizationComplete || isFetchingData || isExpansionLoading}
          getAccessToken={getAccessToken}
          onExpandNode={handleNormalExpansion}
          onExpandVehicles={handleVehicleExpansion}
          setIsFetchingData={setIsFetchingData}
          expandedNodes={normallyExpandedNodes}
          expandedVehicleNodes={expandedVehicleNodes}
          sourceNodeId={graphMetaData.sourceNodeId}
          enableVehicleExpansion={enableVehicleExpansion}
          onShowStreetView={(location, address) => setStreetViewData({ location, address })}
          onMonitorCompany={props.onMonitorCompany}
          onWatchlistChange={handleWatchlistChange}
          enableCompanyComments={enableCompanyComments}
          onSaveCompanyComment={enableCompanyComments ? handleSaveCompanyComment : undefined}
          onDeleteCompanyComment={enableCompanyComments ? handleDeleteCompanyComment : undefined}
        />
      )}
      {streetViewData && (
        <StreetViewPopup
          open={!!streetViewData}
          onClose={() => setStreetViewData(null)}
          location={streetViewData.location}
          address={streetViewData.address}
        />
      )}
      <Snackbar open={!!hiddenBranchNotice} autoHideDuration={8000} onClose={() => setHiddenBranchNotice(null)}
        message={t('graph.investigation.branchHidden', { count: hiddenBranchNotice?.count || 0 })}
        action={<Button color="inherit" size="small" onClick={undoHiddenBranch}>{t('graph.investigation.undo')}</Button>}
      />
    </>
  );
});

OrgGraphVis.displayName = 'OrgGraphVis';

export default OrgGraphVis;
